import { useState, useEffect, useRef, useMemo } from "react";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, ReferenceLine } from "recharts";
import {
  ClerkProvider,
  useClerk,
  useUser,
  useSignIn,
  useSignUp,
} from "@clerk/clerk-react";
import {
  doc, getDoc, setDoc, updateDoc, deleteDoc,
  collection, getDocs,
} from "firebase/firestore";
import { db, logEvent } from "./firebase/config";

const CLERK_KEY = process.env.REACT_APP_CLERK_PUBLISHABLE_KEY;

// ─── DISCIPLINES ────────────────────────────────────────────────────
const CATEGORIES = [
  { id:"sprint",  name:"Sprints",        icon:"⚡", color:"#FFD23F",
    disciplines:[
      {id:"40m",  name:"40 m",  unit:"sec",lowerBetter:true, hint:"e.g. 7.10"},
      {id:"60m",  name:"60 m",  unit:"sec",lowerBetter:true, hint:"e.g. 9.50"},
      {id:"100m", name:"100 m", unit:"sec",lowerBetter:true, hint:"e.g. 15.00"},
      {id:"200m", name:"200 m", unit:"sec",lowerBetter:true, hint:"e.g. 34.00"},
      {id:"400m", name:"400 m", unit:"sec",lowerBetter:true, hint:"e.g. 78.00"},
    ]},
  { id:"middle",  name:"Middle Distance", icon:"🏃", color:"#FF6B35",
    disciplines:[
      {id:"800m",  name:"800 m",  unit:"sec",lowerBetter:true, hint:"e.g. 2:50"},
      {id:"1500m", name:"1500 m", unit:"sec",lowerBetter:true, hint:"e.g. 6:30"},
      {id:"3000m", name:"3000 m", unit:"sec",lowerBetter:true, hint:"e.g. 14:00"},
    ]},
  { id:"road",    name:"Road Running",   icon:"🛣️", color:"#38BDF8",
    disciplines:[
      {id:"5km",    name:"5 km",         unit:"sec",lowerBetter:true, hint:"e.g. 25:30"},
      {id:"10km",   name:"10 km",        unit:"sec",lowerBetter:true, hint:"e.g. 55:00"},
      {id:"half",   name:"Half Marathon",unit:"sec",lowerBetter:true, hint:"e.g. 2:05:00"},
      {id:"full",   name:"Marathon",     unit:"sec",lowerBetter:true, hint:"e.g. 4:30:00"},
    ]},
  { id:"xc",      name:"Cross Country",  icon:"🌲", color:"#4ADE80",
    disciplines:[
      {id:"xc_1km",  name:"XC 1 km",   unit:"sec",lowerBetter:true, hint:"e.g. 4:30"},
      {id:"xc_2km",  name:"XC 2 km",   unit:"sec",lowerBetter:true, hint:"e.g. 9:30"},
      {id:"xc_3km",  name:"XC 3 km",   unit:"sec",lowerBetter:true, hint:"e.g. 14:00"},
      {id:"xc_4km",  name:"XC 4 km",   unit:"sec",lowerBetter:true, hint:"e.g. 19:00"},
      {id:"xc_5km",  name:"XC 5 km",   unit:"sec",lowerBetter:true, hint:"e.g. 25:00"},
      {id:"xc_6km",  name:"XC 6 km",   unit:"sec",lowerBetter:true, hint:"e.g. 30:00"},
      {id:"xc_8km",  name:"XC 8 km",   unit:"sec",lowerBetter:true, hint:"e.g. 42:00"},
      {id:"xc_10km", name:"XC 10 km",  unit:"sec",lowerBetter:true, hint:"e.g. 56:00"},
    ]},
  { id:"hurdles", name:"Hurdles",         icon:"🚧", color:"#FF3CAC",
    disciplines:[
      {id:"60mH", name:"60 m H", unit:"sec",lowerBetter:true, hint:"e.g. 11.00"},
      {id:"80mH", name:"80 m H", unit:"sec",lowerBetter:true, hint:"e.g. 15.00"},
    ]},
  { id:"jumps",   name:"Jumps",           icon:"🦘", color:"#06D6A0",
    disciplines:[
      {id:"long_jump",   name:"Long Jump",   unit:"m",lowerBetter:false, hint:"e.g. 3.35"},
      {id:"high_jump",   name:"High Jump",   unit:"m",lowerBetter:false, hint:"e.g. 1.10"},
      {id:"triple_jump", name:"Triple Jump", unit:"m",lowerBetter:false, hint:"e.g. 7.50"},
    ]},
  { id:"throws",  name:"Throws",          icon:"🥏", color:"#A78BFA",
    disciplines:[
      {id:"ball_throw", name:"Ball Throw", unit:"m",lowerBetter:false, hint:"e.g. 25.00"},
      {id:"shot_put",   name:"Shot Put",   unit:"m",lowerBetter:false, hint:"e.g. 6.50"},
      {id:"javelin",    name:"Javelin",    unit:"m",lowerBetter:false, hint:"e.g. 18.00"},
      {id:"discus",     name:"Discus",     unit:"m",lowerBetter:false, hint:"e.g. 12.00"},
    ]},
];

const ALL_DISCS = CATEGORIES.flatMap(c => c.disciplines.map(d => ({
  ...d, catColor:c.color, catIcon:c.icon, catName:c.name
})));
const AGE_GROUPS = [
  {id:"all",  label:"All"},
  {id:"u8",   label:"< 8",  min:0,  max:7},
  {id:"8-9",  label:"8–9",  min:8,  max:9},
  {id:"10-11",label:"10–11",min:10, max:11},
  {id:"12-13",label:"12–13",min:12, max:13},
  {id:"14-15",label:"14–15",min:14, max:15},
  {id:"16-17",label:"16–17",min:16, max:17},
  {id:"18-24",label:"18–24",min:18, max:24},
  {id:"25-34",label:"25–34",min:25, max:34},
  {id:"35-44",label:"35–44",min:35, max:44},
  {id:"45-54",label:"45–54",min:45, max:54},
  {id:"55+",  label:"55+",  min:55, max:999},
];
const AVATAR_COLORS = ["#FF6B35","#FFD23F","#06D6A0","#FF3CAC","#A78BFA","#38BDF8","#FB7185","#34D399"];
const AVATARS = ["🏃","⚡","🏅","🦘","🥇","🔥","🌟","💪","🎯","🚀"];

// ─── UTILS ──────────────────────────────────────────────────────────
function parseVal(id,raw){
  raw=(raw||"").trim();
  if(["800m","1500m","3000m"].includes(id)&&raw.includes(":")){
    const p=raw.split(":");
    if(p.length===2){const v=+p[0]*60+parseFloat(p[1]);return isNaN(v)?null:v;}
    if(p.length===3){const v=+p[0]*3600+ +p[1]*60+parseFloat(p[2]);return isNaN(v)?null:v;}
  }
  const v=parseFloat(raw);return isNaN(v)?null:v;
}
function fmtVal(id,v){
  if(v==null)return"—";
  if(["800m","1500m","3000m"].includes(id)){
    const h=Math.floor(v/3600),m=Math.floor((v%3600)/60),s=Math.round(v%60).toString().padStart(2,"0");
    return h>0?`${h}:${m.toString().padStart(2,"0")}:${s}`:`${m}:${s}`;
  }
  return Number.isInteger(v)?v.toString():v.toFixed(2);
}
function calcPct(d,c,g,s){
  if(c==null||g==null||s==null)return 0;
  if(d.lowerBetter){const w=s*1.12;return Math.min(100,Math.max(0,((w-c)/(w-g))*100));}
  const w=s*0.88;return Math.min(100,Math.max(0,((c-w)/(g-w))*100));
}
// Returns the athlete's personal best (not the latest result)
function bestVal(disc, recs){
  if(!recs || !recs.length) return null;
  const vals = recs.map(r=>r.v).filter(v=>v!=null);
  if(!vals.length) return null;
  return disc.lowerBetter ? Math.min(...vals) : Math.max(...vals);
}
function emptyDiscData(){
  const o={};ALL_DISCS.forEach(d=>{o[d.id]={records:[],subGoals:[],goalV:null};});return o;
}
const TODAY=()=>new Date().toLocaleDateString("en-GB");


// ─── MONTHLY EXERCISE ───────────────────────────────────────────────
// 12 exercises, one per calendar month — same for every athlete
const MONTHLY_EXERCISES = [
  {id:"m1",  name:"A-Skip",              icon:"🦵", desc:"30m A-skip. Knee up to hip height, ankle dorsiflexed, quick ground contact.", focus:"Sprint mechanics"},
  {id:"m2",  name:"Standing Long Jump",  icon:"🦘", desc:"3 attempts from a standing start. Both feet, full arm swing, stick the landing.", focus:"Explosive power"},
  {id:"m3",  name:"Single-Leg Hops",     icon:"🐇", desc:"10 hops on each leg for distance. Stay tall, minimal ground contact.", focus:"Unilateral strength"},
  {id:"m4",  name:"Plank Hold",          icon:"🪵", desc:"Hold a forearm plank as long as form stays perfect. Flat back, tight core.", focus:"Core stability"},
  {id:"m5",  name:"High Knees",          icon:"⚡", desc:"20m high knees at maximum frequency. Count your contacts.", focus:"Cadence"},
  {id:"m6",  name:"Overhead Ball Throw", icon:"🏐", desc:"Backwards overhead throw for distance. Full body extension.", focus:"Total-body power"},
  {id:"m7",  name:"Bounding",            icon:"🦌", desc:"20m bounding. Maximum distance per stride, drive the opposite arm.", focus:"Stride length"},
  {id:"m8",  name:"Sprint Start",        icon:"🏁", desc:"3-point start + 20m acceleration. Low first steps, gradual rise.", focus:"Acceleration"},
  {id:"m9",  name:"Lunge Walk",          icon:"🚶", desc:"20m walking lunges. Back knee near the ground, torso upright.", focus:"Leg strength"},
  {id:"m10", name:"Hurdle Walkover",     icon:"🚧", desc:"Walk over 5 low hurdles, lead leg then trail leg. Hips square.", focus:"Mobility"},
  {id:"m11", name:"Wall Drill",          icon:"🧱", desc:"Lean on wall, 10 alternating knee drives per leg. Hold the angle.", focus:"Posture"},
  {id:"m12", name:"Standing Triple Jump",icon:"🎯", desc:"Hop-step-jump from standing. Measure total distance.", focus:"Coordination"},
];

function getMonthKey(d){
  const dt = d ? new Date(d) : new Date();
  return `${dt.getFullYear()}-${String(dt.getMonth()+1).padStart(2,"0")}`;
}
function getMonthlyExercise(monthKey){
  const key = monthKey || getMonthKey();
  const m = parseInt(key.split("-")[1],10);
  return MONTHLY_EXERCISES[(m-1) % 12];
}
function monthLabel(monthKey){
  const [y,m] = (monthKey||getMonthKey()).split("-");
  const names=["January","February","March","April","May","June",
               "July","August","September","October","November","December"];
  return `${names[parseInt(m,10)-1]} ${y}`;
}
// Days left in the current month
function daysLeftInMonth(){
  const now=new Date();
  const last=new Date(now.getFullYear(),now.getMonth()+1,0);
  return Math.max(0, Math.ceil((last-now)/(1000*60*60*24)));
}

// Normalise a YouTube / Instagram / Vimeo link; returns null when unsupported
function normaliseVideoUrl(raw){
  const u=(raw||"").trim();
  if(!u) return null;
  if(!/^https?:\/\//i.test(u)) return null;
  const ok=/(youtube\.com|youtu\.be|instagram\.com|vimeo\.com|tiktok\.com)/i.test(u);
  return ok ? u : null;
}
function videoHost(url){
  if(/youtube\.com|youtu\.be/i.test(url)) return {label:"YouTube", icon:"▶️"};
  if(/instagram\.com/i.test(url))          return {label:"Instagram", icon:"📷"};
  if(/vimeo\.com/i.test(url))              return {label:"Vimeo", icon:"🎬"};
  if(/tiktok\.com/i.test(url))             return {label:"TikTok", icon:"🎵"};
  return {label:"Video", icon:"🔗"};
}

// ─── MONTHLY EXERCISE FIRESTORE HELPERS ─────────────────────────────
// Stored at monthlySubmissions/{monthKey} as { entries: [...] }
async function fbGetMonthly(monthKey){
  const s = await getDoc(doc(db,"monthlySubmissions",monthKey));
  return s.exists() ? (s.data().entries||[]) : [];
}
async function fbSubmitMonthly(monthKey, entry){
  const entries = await fbGetMonthly(monthKey);
  const idx = entries.findIndex(e=>e.uid===entry.uid);
  if(idx>=0) entries[idx]=entry; else entries.push(entry);
  await setDoc(doc(db,"monthlySubmissions",monthKey),{entries},{merge:true});
  return entries;
}
async function fbRemoveMonthly(monthKey, uid){
  const entries = (await fbGetMonthly(monthKey)).filter(e=>e.uid!==uid);
  await setDoc(doc(db,"monthlySubmissions",monthKey),{entries},{merge:true});
  return entries;
}
// Housekeeping: drop month documents older than 2 months so video links
// never pile up on the site.
async function fbPurgeOldMonthly(){
  try{
    const cutoff = new Date();
    cutoff.setMonth(cutoff.getMonth()-2);
    const cutoffKey = getMonthKey(cutoff);
    const snap = await getDocs(collection(db,"monthlySubmissions"));
    await Promise.all(snap.docs
      .filter(d=>d.id < cutoffKey)
      .map(d=>deleteDoc(doc(db,"monthlySubmissions",d.id))));
  }catch(e){ /* non-critical */ }
}

// ─── POINTS CALCULATION ─────────────────────────────────────────────
function calcUserPoints(discData, logs, challengesJoinedCount){
  let results=0, goalsSet=0, goalsAchieved=0;
  ALL_DISCS.forEach(d=>{
    const dd=discData[d.id]; if(!dd) return;
    const recs=dd.records||[];
    results+=recs.length;
    if(dd.goalV!=null){
      goalsSet++;
      if(recs.length>0){
        const best=d.lowerBetter?Math.min(...recs.map(r=>r.v)):Math.max(...recs.map(r=>r.v));
        if(d.lowerBetter?best<=dd.goalV:best>=dd.goalV) goalsAchieved++;
      }
    }
  });
  return{results, trainings:logs.length, challengesJoined:challengesJoinedCount,
    goalsSet, goalsAchieved,
    total:results+logs.length+challengesJoinedCount+goalsSet+goalsAchieved};
}

function getWeekKey(dateStr){
  if(!dateStr) return null;
  const parts=dateStr.split("/");
  if(parts.length<3) return null;
  const[d,m,y]=parts;
  const date=new Date(`${y}-${m}-${d}`);
  if(isNaN(date)) return null;
  const jan1=new Date(date.getFullYear(),0,1);
  const week=Math.ceil(((date-jan1)/86400000+jan1.getDay()+1)/7);
  return `${date.getFullYear()}-W${String(week).padStart(2,"0")}`;
}

function buildWeeklyPoints(discData, logs, challengeJoinDates){
  const weekMap={};
  const bump=k=>{ if(k) weekMap[k]=(weekMap[k]||0)+1; };
  ALL_DISCS.forEach(d=>(discData[d.id]?.records||[]).forEach(r=>bump(getWeekKey(r.date))));
  logs.forEach(l=>bump(getWeekKey(l.date)));
  challengeJoinDates.forEach(d=>bump(getWeekKey(d)));
  const sorted=Object.entries(weekMap).sort((a,b)=>a[0].localeCompare(b[0]));
  let cum=0;
  return sorted.map(([week,pts])=>({week,pts,cumulative:cum+=pts}));
}

// ─── FIRESTORE HELPERS ───────────────────────────────────────────────
async function fbGetProfile(uid){const s=await getDoc(doc(db,"users",uid));return s.exists()?s.data():null;}
async function fbSaveProfile(uid,p){await setDoc(doc(db,"users",uid),p,{merge:true});}
async function fbGetDiscData(uid){const s=await getDoc(doc(db,"userDisc",uid));return s.exists()?s.data():emptyDiscData();}
async function fbSaveDiscData(uid,d){await setDoc(doc(db,"userDisc",uid),d);}
async function fbUpdateDisc(uid,discId,data){
  await updateDoc(doc(db,"userDisc",uid),{[discId]:data}).catch(async()=>{
    await setDoc(doc(db,"userDisc",uid),{[discId]:data},{merge:true});
  });
}
async function fbPublishLb(uid,profile,discData,totalPoints){
  const bests={};
  ALL_DISCS.forEach(d=>{
    const r=(discData[d.id]?.records||[]);if(!r.length)return;
    const v=r.map(x=>x.v);bests[d.id]=d.lowerBetter?Math.min(...v):Math.max(...v);
  });
  await setDoc(doc(db,"leaderboard",uid),{
    name:profile.name,age:parseInt(profile.age)||null,
    country:profile.country||"Unknown",avatar:profile.avatar,
    color:profile.color,bests,
    totalPoints:totalPoints||0,
    updatedAt:TODAY()
  },{merge:true});
}
async function fbLoadLb(){
  const s=await getDocs(collection(db,"leaderboard"));
  return s.docs.map(d=>({id:d.id,...d.data()}));
}

// ─── TRAINING LOG HELPERS ──────────────────────────────────────────
async function fbGetTrainingLog(uid){
  const s=await getDoc(doc(db,"userTrainingLog",uid));
  return s.exists()?(s.data().logs||[]):[];
}
async function fbSaveTrainingLog(uid,logs){
  await setDoc(doc(db,"userTrainingLog",uid),{logs},{merge:true});
}

// ─── COMPETITIONS HELPERS ──────────────────────────────────────────
async function fbGetCompetitions(uid){
  const s=await getDoc(doc(db,"userCompetitions",uid));
  return s.exists()?(s.data().competitions||[]):[];
}
async function fbSaveCompetitions(uid,competitions){
  await setDoc(doc(db,"userCompetitions",uid),{competitions},{merge:true});
}

// ─── POINTS HELPERS ─────────────────────────────────────────────────
async function fbLoadPointsLb(){
  const s=await getDocs(collection(db,"leaderboard"));
  return s.docs
    .map(d=>({id:d.id,...d.data()}))
    .filter(e=>e.totalPoints>0)
    .sort((a,b)=>(b.totalPoints||0)-(a.totalPoints||0));
}

// ─── CHALLENGE HELPERS ──────────────────────────────────────────────
async function fbCreateChallenge(data){
  const ref = doc(collection(db,"challenges"));
  await setDoc(ref,{
    title:data.title, desc:data.desc||"",
    creatorUid:data.creatorUid, creatorName:data.creatorName,
    creatorAvatar:data.creatorAvatar, creatorColor:data.creatorColor,
    id:ref.id, createdAt:TODAY(), participants:0, entries:[]
  });
  return ref.id;
}
async function fbLoadChallenges(){
  const s=await getDocs(collection(db,"challenges"));
  return s.docs.map(d=>({...d.data(),id:d.id}));
}
async function fbJoinChallenge(challengeId,uid,profile,note,resultText){
  const ref=doc(db,"challenges",challengeId);
  const snap=await getDoc(ref);
  if(!snap.exists())return;
  const data=snap.data();
  const entries=data.entries||[];
  entries.push({uid,name:profile.name,avatar:profile.avatar,color:profile.color,
    age:profile.age,country:profile.country,note,resultText:resultText||"",date:TODAY()});
  await updateDoc(ref,{entries,participants:entries.length});
}
async function fbGetChallenge(id){
  const s=await getDoc(doc(db,"challenges",id));
  return s.exists()?{...s.data(),id:s.id}:null;
}

// ─── CSS ────────────────────────────────────────────────────────────
const CSS=`
  @import url('https://fonts.googleapis.com/css2?family=Archivo+Black&family=Barlow:wght@400;600&display=swap');
  *,*::before,*::after{box-sizing:border-box;}
  body{margin:0;background:#0d0d2b;}
  button{cursor:pointer;border:none;outline:none;-webkit-tap-highlight-color:transparent;}
  input,textarea{outline:none;}textarea{resize:none;}
  @keyframes fi{from{opacity:0;transform:translateY(8px)}to{opacity:1;transform:none}}.fi{animation:fi .3s ease both;}
  @keyframes pop{0%{transform:scale(.8);opacity:0}70%{transform:scale(1.06)}100%{transform:scale(1);opacity:1}}.pop{animation:pop .35s ease both;}
  @keyframes pl{0%,100%{opacity:1}50%{opacity:.55}}.pl{animation:pl 1.3s infinite;}
  @keyframes toast{0%{opacity:0;transform:translateX(-50%) translateY(16px)}15%{opacity:1;transform:translateX(-50%) translateY(0)}85%{opacity:1}100%{opacity:0}}.toast{animation:toast 2.5s ease forwards;}
  ::-webkit-scrollbar{width:4px}::-webkit-scrollbar-thumb{background:rgba(255,255,255,.12);border-radius:99px}
  input[type=number]::-webkit-inner-spin-button{-webkit-appearance:none;}
`;

// ─── SHARED UI ──────────────────────────────────────────────────────
function Btn({onClick,bg,color,border,children,style={},disabled}){
  return(
    <button onClick={onClick} disabled={disabled}
      style={{minHeight:52,display:"flex",alignItems:"center",justifyContent:"center",gap:8,
        border:border||"none",background:bg||"transparent",color:color||"#fff",
        borderRadius:14,padding:"0 20px",fontFamily:"'Archivo Black',sans-serif",fontSize:15,
        cursor:disabled?"not-allowed":"pointer",opacity:disabled?.55:1,transition:"opacity .15s",...style}}
      onTouchStart={e=>e.currentTarget.style.opacity=".7"}
      onTouchEnd={e=>e.currentTarget.style.opacity="1"}>
      {children}
    </button>
  );
}
function IconBtn({onClick,children,style={}}){
  return(
    <button onClick={onClick}
      style={{minWidth:44,minHeight:44,display:"flex",alignItems:"center",
        justifyContent:"center",background:"none",border:"none",padding:0,...style}}
      onTouchStart={e=>e.currentTarget.style.transform="scale(.85)"}
      onTouchEnd={e=>e.currentTarget.style.transform="scale(1)"}>
      {children}
    </button>
  );
}
function Field({value,onChange,onEnter,placeholder,type="text",style={},maxLength,inputMode}){
  return(
    <input type={type} value={value}
      onChange={e=>onChange(e.target.value)}
      onKeyDown={e=>e.key==="Enter"&&onEnter&&onEnter()}
      placeholder={placeholder} maxLength={maxLength} inputMode={inputMode}
      style={{minHeight:52,background:"rgba(255,255,255,.07)",border:"1px solid rgba(255,255,255,.12)",
        borderRadius:12,padding:"0 16px",color:"#fff",fontFamily:"Barlow,sans-serif",
        fontSize:16,width:"100%",...style}}/>
  );
}
function ChartTip({active,payload,label,discId}){
  if(!active||!payload?.length)return null;
  return(
    <div style={{background:"#12122a",border:"1px solid rgba(255,255,255,.15)",borderRadius:8,
      padding:"8px 14px",fontSize:13,fontFamily:"Barlow,sans-serif"}}>
      <div style={{opacity:.78}}>{label}</div>
      <div style={{color:"#fff",fontWeight:600}}>{fmtVal(discId,payload[0].value)}</div>
    </div>
  );
}
// eslint-disable-next-line no-unused-vars
function SaveToast({show}){
  if(!show)return null;
  return(
    <div className="toast" style={{position:"fixed",bottom:28,left:"50%",
      background:"#06D6A0",color:"#000",borderRadius:99,padding:"10px 22px",
      fontFamily:"'Archivo Black',sans-serif",fontSize:13,zIndex:999,
      pointerEvents:"none",whiteSpace:"nowrap",boxShadow:"0 4px 20px rgba(6,214,160,.4)"}}>
      ✓ Saved
    </div>
  );
}
function ErrBox({msg}){
  if(!msg)return null;
  return(
    <div style={{fontSize:13,color:"rgba(255,140,140,.95)",fontFamily:"Barlow,sans-serif",
      marginBottom:16,padding:"11px 14px",background:"rgba(255,80,80,.1)",borderRadius:10,lineHeight:1.5}}>
      {msg}
    </div>
  );
}

// ─── AUTH SCREEN (Clerk OTP) ─────────────────────────────────────────
function OTPBoxes({value, onChange, onComplete}){
  // useRef in array is not allowed — use a single ref with array
  const containerRef = useRef(null);
  const digits = value.split("").concat(Array(6).fill("")).slice(0,6);

  function getInput(i){
    return containerRef.current?.querySelectorAll("input")[i];
  }

  function handleKey(i, e){
    if(e.key==="Backspace"){
      e.preventDefault();
      const nd = digits.map((d,j)=>j===i?"":d).join("").slice(0,6);
      onChange(nd);
      if(i>0) getInput(i-1)?.focus();
    } else if(e.key==="ArrowLeft" && i>0){
      getInput(i-1)?.focus();
    } else if(e.key==="ArrowRight" && i<5){
      getInput(i+1)?.focus();
    } else if(e.key==="Enter"){
      onComplete();
    }
  }

  function handleChange(i, v){
    const digit = v.replace(/\D/g,"").slice(-1);
    const arr = digits.slice();
    arr[i] = digit;
    const nd = arr.join("").slice(0,6);
    onChange(nd);
    if(digit && i<5) getInput(i+1)?.focus();
    if(nd.length===6) onComplete();
  }

  function handlePaste(e){
    e.preventDefault();
    const pasted = e.clipboardData.getData("text").replace(/\D/g,"").slice(0,6);
    onChange(pasted);
    const focusIdx = Math.min(pasted.length, 5);
    setTimeout(()=>getInput(focusIdx)?.focus(), 0);
    if(pasted.length===6) onComplete();
  }

  return(
    <div ref={containerRef} style={{display:"flex",gap:6,marginBottom:20,justifyContent:"center"}}>
      {digits.map((d,i)=>(
        <input key={i}
          type="text" inputMode="numeric" maxLength={1}
          value={d}
          onChange={e=>handleChange(i,e.target.value)}
          onKeyDown={e=>handleKey(i,e)}
          onPaste={handlePaste}
          onFocus={e=>e.target.select()}
          style={{
            flex:1, maxWidth:48, height:56, textAlign:"center",
            background: d ? "rgba(255,107,53,.15)" : "rgba(255,255,255,.07)",
            border: d ? "2px solid #FF6B35" : "1.5px solid rgba(255,255,255,.15)",
            borderRadius:12, color:"#fff",
            fontFamily:"'Archivo Black',sans-serif", fontSize:26,
            transition:"all .15s", outline:"none", flexShrink:0,
            WebkitTapHighlightColor:"transparent"
          }}/>
      ))}
    </div>
  );
}

function AuthScreen({onSuccess}){
  const [step, setStep]         = useState("email");
  const [mode, setMode]         = useState("signin");
  const [email, setEmail]       = useState("");
  const [code, setCode]         = useState("");
  const [loading, setLoading]   = useState(false);
  const [error, setError]       = useState("");
  const [verified, setVerified] = useState(false);

  const { signIn, setActive: setSignInActive, isLoaded: siLoaded } = useSignIn();
  const { signUp, setActive: setSignUpActive, isLoaded: suLoaded } = useSignUp();

  async function sendCode(){
    if(!email.trim()||!siLoaded||!suLoaded) return;
    setLoading(true); setError("");
    try{
      // Try sign-in with email_code strategy (create + prepare in one step)
      await signIn.create({identifier: email.trim(), strategy: "email_code"});
      setMode("signin");
      setStep("otp");
    }catch(e){
      const errCode = e?.errors?.[0]?.code;
      if(errCode==="form_identifier_not_found"){
        // New user — create account automatically
        try{
          await signUp.create({emailAddress: email.trim()});
          await signUp.prepareEmailAddressVerification({strategy:"email_code"});
          setMode("signup");
          setStep("otp");
        }catch(e2){
          const msg2 = e2?.errors?.[0]?.longMessage||e2?.errors?.[0]?.message||e2?.message||"Failed to create account.";
          setError(msg2);
        }
      } else {
        const msg = e?.errors?.[0]?.longMessage||e?.errors?.[0]?.message||e?.message||"Could not send code.";
        setError(msg);
      }
    }
    setLoading(false);
  }

  async function verifyCode(){
    if(code.length<6||loading||verified) return;
    setLoading(true); setError("");
    try{
      let result;
      if(mode==="signin"){
        result = await signIn.attemptFirstFactor({strategy:"email_code", code});
        if(result.status==="complete"){
          setVerified(true);
          await setSignInActive({session:result.createdSessionId});
          onSuccess();
          return;
        }
      } else {
        result = await signUp.attemptEmailAddressVerification({code});
        if(result.status==="complete"){
          setVerified(true);
          await setSignUpActive({session:result.createdSessionId});
          onSuccess();
          return;
        }
      }
      setError("Unexpected status: "+result?.status+". Please try again.");
    }catch(e){
      console.error("Verify error:", e?.errors, e?.message);
      const msg = e?.errors?.[0]?.longMessage||e?.errors?.[0]?.message||e?.message||"Incorrect code. Please try again.";
      setError(msg);
      // Do NOT clear code so user sees what they entered
    }
    setLoading(false);
  }

  if(verified) return(
    <div style={{minHeight:"100vh",background:"linear-gradient(160deg,#0d0d2b,#0f1e35)",
      display:"flex",alignItems:"center",justifyContent:"center"}}>
      <div style={{textAlign:"center"}}>
        <div style={{fontSize:52,marginBottom:16}}>🏅</div>
        <div style={{color:"rgba(255,255,255,.88)",fontFamily:"Barlow,sans-serif",fontSize:15}} className="pl">
          Signing in…
        </div>
      </div>
    </div>
  );

  return(
    <div style={{minHeight:"100vh",background:"linear-gradient(160deg,#0d0d2b,#0f1e35)",
      color:"#fff",fontFamily:"'Archivo Black','Arial Black',sans-serif",maxWidth:480,margin:"0 auto",padding:"0 20px"}}>

      <div style={{paddingTop:72,textAlign:"center",marginBottom:40}}>
        <div style={{fontSize:56,marginBottom:14}}>🦊</div>
        <div style={{fontSize:32,letterSpacing:-1,fontWeight:600}}>Foxvelo</div>
        <div style={{fontSize:15,color:"rgba(255,255,255,.65)",fontFamily:"Barlow,sans-serif",marginTop:8,fontWeight:600}}>
          Track · Compete · Improve
        </div>
      </div>

      <div style={{background:"rgba(255,255,255,.05)",border:"1px solid rgba(255,255,255,.1)",
        borderRadius:20,padding:"28px 24px"}} className="fi">

        {step==="email"&&(
          <>
            <div style={{fontSize:18,marginBottom:6}}>Sign in or create account</div>
            <div style={{fontSize:13,opacity:.78,fontFamily:"Barlow,sans-serif",marginBottom:24,lineHeight:1.6}}>
              Enter your email — we'll send a 6-digit code. No password needed.
            </div>
            <ErrBox msg={error}/>
            <div style={{fontSize:13,letterSpacing:2,opacity:.75,fontFamily:"Barlow,sans-serif",marginBottom:8}}>
              EMAIL ADDRESS
            </div>
            <Field value={email} onChange={setEmail} onEnter={sendCode}
              placeholder="your@email.com" type="email" style={{marginBottom:16}}/>
            <Btn onClick={sendCode} disabled={loading||!email.trim()}
              bg="linear-gradient(90deg,#FF6B35,#FF3CAC)" color="#fff"
              style={{width:"100%",minHeight:56,fontSize:16}}>
              {loading?<span className="pl">Sending…</span>:"✉️ Send Code"}
            </Btn>
          </>
        )}

        {step==="otp"&&(
          <>
            <div style={{fontSize:18,marginBottom:6}}>Enter the code</div>
            <div style={{fontSize:13,opacity:.78,fontFamily:"Barlow,sans-serif",marginBottom:4,lineHeight:1.6}}>
              We sent a 6-digit code to
            </div>
            <div style={{fontSize:14,color:"#FF6B35",fontFamily:"Barlow,sans-serif",
              marginBottom:24,wordBreak:"break-all"}}>{email}</div>
            <ErrBox msg={error}/>
            <OTPBoxes value={code} onChange={setCode} onComplete={verifyCode}/>
            <Btn onClick={verifyCode} disabled={loading||code.length<6}
              bg="linear-gradient(90deg,#FF6B35,#FF3CAC)" color="#fff"
              style={{width:"100%",minHeight:56,fontSize:16}}>
              {loading?<span className="pl">Verifying…</span>:"✓ Confirm"}
            </Btn>
            <div style={{textAlign:"center",marginTop:18,display:"flex",justifyContent:"center",gap:20}}>
              <button onClick={()=>{setStep("email");setCode("");setError("");}}
                style={{background:"none",color:"rgba(255,255,255,.72)",
                  fontFamily:"Barlow,sans-serif",fontSize:13}}>
                ← Change email
              </button>
              <button onClick={sendCode}
                style={{background:"none",color:"rgba(255,255,255,.72)",
                  fontFamily:"Barlow,sans-serif",fontSize:13}}>
                Resend code
              </button>
            </div>
            <div style={{marginTop:16,fontSize:11,opacity:.78,fontFamily:"Barlow,sans-serif",
              textAlign:"center",lineHeight:1.6}}>
              Check spam folder if you don't see it.<br/>Code expires in 10 minutes.
            </div>
          </>
        )}
      </div>

      {/* World Age Records link */}
      <div style={{textAlign:"center",marginTop:28,paddingBottom:40}}>
        <a href="/records"
          style={{color:"rgba(255,255,255,.55)",fontFamily:"Barlow,sans-serif",
            fontSize:13,textDecoration:"none",letterSpacing:.3}}>
          🌍 Explore World Age Records
        </a>
      </div>
    </div>
  );
}


// ─── CREATE PROFILE ──────────────────────────────────────────────────
function CreateProfile({onSave}){
  const [name,setName]       = useState("");
  const [birthYear,setBYear] = useState("");
  const [country,setCountry] = useState("");
  const [geoLoading,setGeoL] = useState(false);
  const [avatar,setAvatar]   = useState("🏃");
  const [color,setColor]     = useState(AVATAR_COLORS[0]);
  const [step,setStep]       = useState(1);
  const [saving,setSaving]   = useState(false);

  // Auto-detect country on mount
  useEffect(()=>{
    setGeoL(true);
    fetch("https://ipapi.co/json/")
      .then(r=>r.json())
      .then(d=>{
        if(d.country_name) setCountry(d.country_name);
      })
      .catch(()=>{})
      .finally(()=>setGeoL(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  },[]);

  // Birth year options: current year down to 1940
  const currentYear = new Date().getFullYear();
  const years = [];
  for(let y=currentYear; y>=1940; y--) years.push(y);

  // Calculate age from birth year
  const age = birthYear ? String(currentYear - parseInt(birthYear)) : "";

  async function finish(){
    if(!name.trim()) return;
    setSaving(true);
    await onSave({
      name:name.trim(),
      age,
      birthYear: birthYear ? parseInt(birthYear) : null,
      country:country.trim()||"Unknown",
      avatar, color
    });
    setSaving(false);
  }

  const selectStyle = {
    width:"100%", minHeight:56,
    background:"rgba(255,255,255,.07)",
    border:"1px solid rgba(255,255,255,.15)",
    borderRadius:14, padding:"0 16px",
    color: birthYear?"#fff":"rgba(255,255,255,.4)",
    fontFamily:"Barlow,sans-serif", fontSize:17,
    outline:"none", appearance:"none",
    WebkitAppearance:"none",
    cursor:"pointer",
    backgroundImage:`url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='8' viewBox='0 0 12 8'%3E%3Cpath d='M1 1l5 5 5-5' stroke='rgba(255,255,255,.5)' stroke-width='1.5' fill='none'/%3E%3C/svg%3E")`,
    backgroundRepeat:"no-repeat",
    backgroundPosition:"right 16px center",
  };

  return(
    <div style={{minHeight:"100vh",background:"linear-gradient(160deg,#0d0d2b,#0f1e35)",
      color:"#fff",fontFamily:"'Archivo Black','Arial Black',sans-serif",
      maxWidth:480,margin:"0 auto",padding:"0 20px 40px"}}>
      <div style={{paddingTop:48,marginBottom:32}}>
        <div style={{fontSize:28,marginBottom:8}}>Create profile</div>
        <div style={{fontSize:15,opacity:.65,fontFamily:"Barlow,sans-serif",lineHeight:1.5}}>
          Set up your athlete profile to get started
        </div>
      </div>

      {step===1&&(
        <div className="fi">
          {/* Name */}
          <div style={{marginBottom:18}}>
            <div style={{fontSize:14,letterSpacing:1.5,fontWeight:600,
              color:"rgba(255,255,255,.7)",fontFamily:"Barlow,sans-serif",marginBottom:10}}>
              ATHLETE NAME *
            </div>
            <Field value={name} onChange={setName} placeholder="e.g. Maxim"
              style={{fontSize:18,fontWeight:600}}/>
          </div>

          {/* Birth year */}
          <div style={{marginBottom:18}}>
            <div style={{fontSize:14,letterSpacing:1.5,fontWeight:600,
              color:"rgba(255,255,255,.7)",fontFamily:"Barlow,sans-serif",marginBottom:10}}>
              YEAR OF BIRTH
            </div>
            <select value={birthYear} onChange={e=>setBYear(e.target.value)}
              style={selectStyle}>
              <option value="">Select year…</option>
              {years.map(y=>(
                <option key={y} value={y}
                  style={{background:"#1a1a3e",color:"#fff"}}>{y}</option>
              ))}
            </select>
            {birthYear&&(
              <div style={{fontSize:14,color:"rgba(255,255,255,.88)",
                fontFamily:"Barlow,sans-serif",marginTop:8}}>
                Age: <strong style={{color:"#FF6B35"}}>{age} years old</strong>
              </div>
            )}
          </div>

          {/* Country */}
          <div style={{marginBottom:36}}>
            <div style={{fontSize:14,letterSpacing:1.5,fontWeight:600,
              color:"rgba(255,255,255,.7)",fontFamily:"Barlow,sans-serif",marginBottom:10,
              display:"flex",alignItems:"center",gap:8}}>
              COUNTRY
              {geoLoading&&<span style={{fontSize:11,color:"rgba(255,255,255,.78)",
                fontFamily:"Barlow,sans-serif",fontWeight:400}} className="pl">
                Detecting…
              </span>}
            </div>
            <Field value={country} onChange={setCountry} placeholder="e.g. Finland"
              style={{fontSize:17}}/>
            {country&&!geoLoading&&(
              <div style={{fontSize:13,color:"rgba(255,255,255,.85)",
                fontFamily:"Barlow,sans-serif",marginTop:8}}>
                📍 Detected from your location
              </div>
            )}
          </div>

          <Btn onClick={()=>name.trim()&&setStep(2)}
            bg={name.trim()?"linear-gradient(90deg,#FF6B35,#FF3CAC)":"rgba(255,255,255,.08)"}
            color={name.trim()?"#fff":"rgba(255,255,255,.3)"}
            style={{width:"100%",minHeight:60,fontSize:18}}>
            Next →
          </Btn>
        </div>
      )}

      {step===2&&(
        <div className="fi">
          <div style={{display:"flex",justifyContent:"center",marginBottom:28}}>
            <div style={{width:96,height:96,borderRadius:"50%",background:color,
              display:"flex",alignItems:"center",justifyContent:"center",fontSize:44,
              border:"4px solid rgba(255,255,255,.25)",
              boxShadow:`0 8px 32px ${color}55`}} className="pop">{avatar}</div>
          </div>

          <div style={{marginBottom:24}}>
            <div style={{fontSize:14,letterSpacing:1.5,fontWeight:600,
              color:"rgba(255,255,255,.7)",fontFamily:"Barlow,sans-serif",marginBottom:14}}>
              CHOOSE AVATAR
            </div>
            <div style={{display:"grid",gridTemplateColumns:"repeat(5,1fr)",gap:10}}>
              {AVATARS.map(a=>(
                <button key={a} onClick={()=>setAvatar(a)}
                  style={{minHeight:60,fontSize:28,borderRadius:16,
                    background:avatar===a?"rgba(255,255,255,.18)":"rgba(255,255,255,.06)",
                    border:avatar===a?`2px solid ${color}`:"2px solid rgba(255,255,255,.1)",
                    transition:".15s",cursor:"pointer"}}>
                  {a}
                </button>
              ))}
            </div>
          </div>

          <div style={{marginBottom:36}}>
            <div style={{fontSize:14,letterSpacing:1.5,fontWeight:600,
              color:"rgba(255,255,255,.7)",fontFamily:"Barlow,sans-serif",marginBottom:14}}>
              PROFILE COLOUR
            </div>
            <div style={{display:"flex",gap:12,flexWrap:"wrap"}}>
              {AVATAR_COLORS.map(c=>(
                <button key={c} onClick={()=>setColor(c)}
                  style={{width:48,height:48,borderRadius:"50%",background:c,
                    border:color===c?"3px solid #fff":"3px solid transparent",
                    boxShadow:color===c?`0 0 16px ${c}99`:"none",
                    transition:".15s",cursor:"pointer"}}/>
              ))}
            </div>
          </div>

          <Btn onClick={finish} disabled={saving}
            bg={`linear-gradient(90deg,${color},${color}cc)`} color="#fff"
            style={{width:"100%",minHeight:60,fontSize:18}}>
            {saving?<span className="pl">Saving…</span>:"🏅 Create Profile"}
          </Btn>
        </div>
      )}
    </div>
  );
}


// ─── DETAIL VIEW ─────────────────────────────────────────────────────
function DetailView({disc,dData,uid,profile,onBack,onSaved,onDiscDataChange}){
  const [resIn,setResIn]     = useState("");
  const [sgIn,setSgIn]       = useState("");
  const [goalIn,setGoalIn]   = useState(dData.goalV!=null?fmtVal(disc.id,dData.goalV):"");
  const [localData,setLocal] = useState(dData);
  const [flash,setFlash]     = useState(false);
  const [editIdx,setEditIdx] = useState(null); // index of record being edited
  const [editVal,setEditVal] = useState("");

  const recs=localData.records,curV=bestVal(disc,recs);
  const goalV=localData.goalV,startV=recs.length?recs[0].v:null;
  const p=calcPct(disc,curV,goalV,startV),C=disc.catColor;

  async function save(nd){
    setLocal(nd);
    await fbUpdateDisc(uid,disc.id,nd);
    onDiscDataChange(disc.id,nd);
    onSaved();
  }
  async function saveGoal(){const v=parseVal(disc.id,goalIn);if(v==null)return;logEvent("set_goal",{discipline:disc.id});await save({...localData,goalV:v});}
  async function clearGoal(){setGoalIn("");await save({...localData,goalV:null});}
  async function addRecord(){
    const v=parseVal(disc.id,resIn);if(v==null)return;
    const nd={...localData,records:[...localData.records,{date:TODAY(),v}]};
    logEvent("add_result",{discipline:disc.id,unit:disc.unit});
    await save(nd);
    if(goalV!=null&&(disc.lowerBetter?v<=goalV:v>=goalV)){setFlash(true);setTimeout(()=>setFlash(false),4000);}
    setResIn("");
  }
  async function deleteRecord(idx){
    const recs2=[...localData.records];
    recs2.splice(recs.length-1-idx,1); // reversed index
    await save({...localData,records:recs2});
  }
  async function saveEditRecord(){
    const v=parseVal(disc.id,editVal);if(v==null)return;
    const recs2=[...localData.records];
    const realIdx=recs.length-1-editIdx;
    recs2[realIdx]={...recs2[realIdx],v};
    await save({...localData,records:recs2});
    setEditIdx(null);setEditVal("");
  }
  async function addSg(){const v=parseVal(disc.id,sgIn);if(v==null)return;await save({...localData,subGoals:[...localData.subGoals,{v,done:false}]});setSgIn("");}
  async function toggleSg(i){const sg=localData.subGoals.map((g,j)=>j===i?{...g,done:!g.done}:g);await save({...localData,subGoals:sg});}
  async function deleteSg(i){await save({...localData,subGoals:localData.subGoals.filter((_,j)=>j!==i)});}

  const chartData=recs.map(r=>({date:r.date,value:r.v}));

  return(
    <div style={{minHeight:"100vh",background:"linear-gradient(160deg,#0d0d2b,#0f1e35)",
      color:"#fff",fontFamily:"'Archivo Black','Arial Black',sans-serif",maxWidth:480,margin:"0 auto"}}>
      <div style={{background:`linear-gradient(90deg,${C},${C}88)`,padding:"16px 16px 14px",
        display:"flex",alignItems:"center",gap:12,position:"relative",overflow:"hidden"}}>
        <div style={{position:"absolute",top:-40,right:-20,width:130,height:130,
          background:"rgba(255,255,255,.07)",borderRadius:"50%",pointerEvents:"none"}}/>
        <Btn onClick={onBack} bg="rgba(0,0,0,.25)" color="#fff"
          style={{borderRadius:12,padding:"0 16px",fontSize:15,flexShrink:0,minHeight:46}}>← Back</Btn>
        <div>
          <div style={{fontSize:14,letterSpacing:1.5,color:"rgba(255,255,255,.8)",
            fontFamily:"Barlow,sans-serif",fontWeight:600}}>
            {disc.catIcon} {disc.catName.toUpperCase()}
          </div>
          <div style={{fontSize:22,fontWeight:600}}>{disc.name}</div>
        </div>
      </div>

      <div style={{padding:"14px 14px 50px"}}>
        <div className="fi">
          {flash&&(
            <div style={{background:"linear-gradient(90deg,#06D6A0,#FFD23F)",borderRadius:14,
              padding:"16px 18px",marginBottom:14,color:"#000",fontSize:16,
              display:"flex",gap:10,alignItems:"center",fontWeight:600}}>
              🎉 GOAL ACHIEVED! Well done, {profile.name}!
            </div>
          )}

          {/* Score card */}
          <div style={{background:"rgba(255,255,255,.06)",border:`1.5px solid ${C}44`,
            borderRadius:18,padding:"20px 18px",marginBottom:12}}>
            <div style={{display:"flex",justifyContent:"space-between",marginBottom:goalV!=null?20:10}}>
              <div>
                <div style={{fontSize:14,letterSpacing:1.5,color:"rgba(255,255,255,.65)",
                  fontFamily:"Barlow,sans-serif",fontWeight:600,marginBottom:4}}>PERSONAL BEST</div>
                <div style={{fontSize:48,color:C,lineHeight:1,fontWeight:600}}>{curV!=null?fmtVal(disc.id,curV):"—"}</div>
                <div style={{fontSize:13,color:"rgba(255,255,255,.85)",fontFamily:"Barlow,sans-serif",marginTop:2}}>{disc.unit}</div>
              </div>
              <div style={{textAlign:"right"}}>
                <div style={{fontSize:14,letterSpacing:1.5,color:"rgba(255,255,255,.65)",
                  fontFamily:"Barlow,sans-serif",fontWeight:600,marginBottom:4}}>GOAL</div>
                <div style={{fontSize:48,lineHeight:1,fontWeight:600,
                  color:goalV!=null?"#fff":"rgba(255,255,255,.25)"}}>
                  {goalV!=null?fmtVal(disc.id,goalV):"?"}
                </div>
                <div style={{fontSize:13,color:"rgba(255,255,255,.85)",fontFamily:"Barlow,sans-serif",marginTop:2}}>{disc.unit}</div>
              </div>
            </div>
            {goalV!=null&&curV!=null&&(
              <>
                <div style={{display:"flex",justifyContent:"space-between",marginBottom:8}}>
                  <span style={{fontSize:13,fontFamily:"Barlow,sans-serif",
                    color:"rgba(255,255,255,.65)",fontWeight:600}}>Progress to goal</span>
                  <span style={{fontSize:14,color:C,fontWeight:600}}>{Math.round(p)}%</span>
                </div>
                <div style={{height:10,background:"rgba(255,255,255,.1)",borderRadius:99,overflow:"hidden"}}>
                  <div style={{height:"100%",width:`${p}%`,background:`linear-gradient(90deg,${C},${C}99)`,
                    borderRadius:99,boxShadow:`0 0 12px ${C}77`,transition:"width 1.1s cubic-bezier(.4,2,.6,1)"}}/>
                </div>
              </>
            )}
          </div>

          {/* Goal section */}
          <div style={{background:"rgba(255,255,255,.05)",border:"1px solid rgba(255,255,255,.1)",
            borderRadius:16,padding:"16px",marginBottom:12}}>
            <div style={{fontSize:13,letterSpacing:1.5,color:"rgba(255,255,255,.7)",
              fontFamily:"Barlow,sans-serif",fontWeight:600,marginBottom:12}}>SET GOAL</div>
            <div style={{display:"flex",gap:8}}>
              <Field value={goalIn} onChange={setGoalIn} onEnter={saveGoal}
                placeholder={disc.hint} style={{flex:1,fontSize:17}}/>
              <Btn onClick={saveGoal} bg={`${C}33`} color={C} border={`1.5px solid ${C}55`}
                style={{borderRadius:12,minWidth:60,fontSize:16}}>✓</Btn>
              {goalV!=null&&(
                <Btn onClick={clearGoal} bg="rgba(255,80,80,.15)" color="rgba(255,120,120,.9)"
                  border="1px solid rgba(255,80,80,.3)"
                  style={{borderRadius:12,minWidth:48,fontSize:16}}>✕</Btn>
              )}
            </div>
          </div>

          {/* Chart */}
          {chartData.length>1&&(
            <div style={{background:"rgba(255,255,255,.04)",border:"1px solid rgba(255,255,255,.08)",
              borderRadius:18,padding:"16px 6px 8px",marginBottom:12}}>
              <div style={{fontSize:13,letterSpacing:1.5,color:"rgba(255,255,255,.7)",
                fontFamily:"Barlow,sans-serif",fontWeight:600,paddingLeft:10,marginBottom:8}}>
                PROGRESS CHART
              </div>
              <ResponsiveContainer width="100%" height={140}>
                <LineChart data={chartData} margin={{left:-16,right:10,top:4}}>
                  <XAxis dataKey="date" tick={{fill:"rgba(255,255,255,.5)",fontSize:14,fontFamily:"Barlow,sans-serif"}} axisLine={false} tickLine={false}/>
                  <YAxis tick={{fill:"rgba(255,255,255,.5)",fontSize:14,fontFamily:"Barlow,sans-serif"}} axisLine={false} tickLine={false} tickFormatter={v=>fmtVal(disc.id,v)}/>
                  <Tooltip content={<ChartTip discId={disc.id}/>}/>
                  {goalV!=null&&<ReferenceLine y={goalV} stroke={C} strokeDasharray="5 3" strokeOpacity={.7}/>}
                  <Line type="monotone" dataKey="value" stroke={C} strokeWidth={2.5} dot={{fill:C,r:5,strokeWidth:0}} activeDot={{r:7}}/>
                </LineChart>
              </ResponsiveContainer>
            </div>
          )}

          {/* Add result */}
          <div style={{background:"rgba(255,255,255,.05)",border:"1px solid rgba(255,255,255,.1)",
            borderRadius:16,padding:"16px",marginBottom:12}}>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:12}}>
              <div style={{fontSize:13,letterSpacing:1.5,color:"rgba(255,255,255,.7)",
                fontFamily:"Barlow,sans-serif",fontWeight:600}}>ADD RESULT</div>
              <div style={{fontSize:12,color:"rgba(255,255,255,.85)",fontFamily:"Barlow,sans-serif"}}>
                ⬆ appears in rankings
              </div>
            </div>
            <div style={{display:"flex",gap:8}}>
              <Field value={resIn} onChange={setResIn} onEnter={addRecord}
                placeholder={disc.hint} style={{flex:1,fontSize:18,fontWeight:600}}/>
              <Btn onClick={addRecord} bg={C} color="#000"
                style={{borderRadius:12,minWidth:60,fontSize:24}}>+</Btn>
            </div>
          </div>

          {/* History with edit/delete */}
          {recs.length>0&&(
            <div style={{background:"rgba(255,255,255,.04)",border:"1px solid rgba(255,255,255,.08)",
              borderRadius:16,padding:"16px",marginBottom:12}}>
              <div style={{fontSize:13,letterSpacing:1.5,color:"rgba(255,255,255,.7)",
                fontFamily:"Barlow,sans-serif",fontWeight:600,marginBottom:12}}>HISTORY</div>
              {[...recs].reverse().slice(0,8).map((r,i)=>(
                <div key={i}>
                  {editIdx===i ? (
                    // Edit mode
                    <div style={{display:"flex",gap:8,minHeight:52,alignItems:"center",
                      marginBottom:6}}>
                      <Field value={editVal} onChange={setEditVal} onEnter={saveEditRecord}
                        placeholder={disc.hint} style={{flex:1,fontSize:16}}/>
                      <Btn onClick={saveEditRecord} bg={C} color="#000"
                        style={{borderRadius:10,minWidth:52,fontSize:14}}>✓</Btn>
                      <Btn onClick={()=>setEditIdx(null)} bg="rgba(255,255,255,.1)"
                        color="rgba(255,255,255,.7)"
                        style={{borderRadius:10,minWidth:44,fontSize:16}}>✕</Btn>
                    </div>
                  ) : (
                    // View mode
                    <div style={{display:"flex",justifyContent:"space-between",
                      alignItems:"center",minHeight:52,
                      borderBottom:i<Math.min(recs.length,8)-1?"1px solid rgba(255,255,255,.06)":"none"}}>
                      <span style={{color:"rgba(255,255,255,.88)",fontSize:14,
                        fontFamily:"Barlow,sans-serif"}}>{r.date}</span>
                      <div style={{display:"flex",alignItems:"center",gap:8}}>
                        <span style={{color:C,fontSize:18,fontWeight:600,fontFamily:"Barlow,sans-serif"}}>
                          {fmtVal(disc.id,r.v)}
                          <span style={{color:"rgba(255,255,255,.82)",fontSize:13,
                            fontWeight:400,marginLeft:4}}>{disc.unit}</span>
                        </span>
                        <button onClick={()=>{setEditIdx(i);setEditVal(fmtVal(disc.id,r.v));}}
                          style={{width:32,height:32,borderRadius:8,
                            background:"rgba(255,255,255,.08)",border:"none",
                            color:"rgba(255,255,255,.88)",fontSize:14,cursor:"pointer"}}>
                          ✎
                        </button>
                        <button onClick={()=>deleteRecord(i)}
                          style={{width:32,height:32,borderRadius:8,
                            background:"rgba(255,80,80,.1)",border:"none",
                            color:"rgba(255,120,120,.8)",fontSize:16,cursor:"pointer"}}>
                          ×
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* Milestones */}
          <div style={{background:"rgba(255,255,255,.04)",border:"1px solid rgba(255,255,255,.08)",
            borderRadius:16,padding:"16px"}}>
            <div style={{fontSize:13,letterSpacing:1.5,color:"rgba(255,255,255,.7)",
              fontFamily:"Barlow,sans-serif",fontWeight:600,marginBottom:12}}>MILESTONES</div>
            {localData.subGoals.length===0&&(
              <div style={{fontSize:14,color:"rgba(255,255,255,.82)",
                fontFamily:"Barlow,sans-serif",marginBottom:12}}>No milestones yet</div>
            )}
            {localData.subGoals.map((sg,i)=>(
              <div key={i} style={{display:"flex",alignItems:"center",minHeight:54,
                borderBottom:i<localData.subGoals.length-1?"1px solid rgba(255,255,255,.07)":"none"}}>
                <IconBtn onClick={()=>toggleSg(i)}>
                  <div style={{width:34,height:34,borderRadius:10,
                    background:sg.done?C:"rgba(255,255,255,.1)",
                    color:sg.done?"#000":"transparent",
                    display:"flex",alignItems:"center",justifyContent:"center",
                    fontSize:17,fontWeight:700,transition:".2s"}}>✓</div>
                </IconBtn>
                <span style={{flex:1,fontFamily:"Barlow,sans-serif",fontSize:16,fontWeight:600,
                  textDecoration:sg.done?"line-through":"none",
                  color:sg.done?"rgba(255,255,255,.45)":"#fff"}}>
                  {fmtVal(disc.id,sg.v)}
                  <span style={{fontSize:13,color:"rgba(255,255,255,.82)",marginLeft:4,fontWeight:400}}>
                    {disc.unit}
                  </span>
                </span>
                <IconBtn onClick={()=>deleteSg(i)}>
                  <span style={{color:"rgba(255,100,100,.7)",fontSize:24}}>×</span>
                </IconBtn>
              </div>
            ))}
            <div style={{display:"flex",gap:8,marginTop:10}}>
              <Field value={sgIn} onChange={setSgIn} onEnter={addSg}
                placeholder={disc.hint} style={{flex:1,fontSize:16}}/>
              <Btn onClick={addSg} bg={`${C}22`} color={C} border={`1.5px solid ${C}44`}
                style={{borderRadius:12,minWidth:60,fontSize:20}}>+</Btn>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function Dashboard({uid,profile,discData,onDiscDataChange,
  onShowHistory,onShowProfile,onShowMonthly,onSignOut}){

  const [tab, setTab]             = useState("progress"); // progress | rankings | challenges
  const [activeCat, setActiveCat] = useState(null);
  const [activeDisc, setActiveDisc] = useState(null);
  const [showSaved, setShowSaved] = useState(false);
  const saveTimer = useRef(null);

  // Rankings state
  const [rankEntries, setRankEntries] = useState([]);
  const [rankLoading, setRankLoading] = useState(false);
  const [rankLoaded, setRankLoaded]   = useState(false);
  const [discId, setDiscId]           = useState("800m");
  const [ageF, setAgeF]               = useState("all");
  const [cntF, setCntF]               = useState("all");

  // Challenges state
  const [challenges, setChallenges]   = useState([]);
  const [chLoading, setChLoading]     = useState(false);
  const [chLoaded, setChLoaded]       = useState(false);
  const [openCh, setOpenCh]           = useState(null);
  const [showCreate, setShowCreate]   = useState(false);
  const [chTab, setChTab]             = useState("latest");

  const discObj = activeDisc ? ALL_DISCS.find(d=>d.id===activeDisc) : null;

  function triggerSaved(){
    if(saveTimer.current) clearTimeout(saveTimer.current);
    setShowSaved(true);
    saveTimer.current = setTimeout(()=>setShowSaved(false), 2500);
  }

  // Lazy load tabs
  useEffect(()=>{
    if(tab==="rankings"&&!rankLoaded){
      setRankLoading(true);
      fbLoadLb().then(e=>{setRankEntries(e);setRankLoading(false);setRankLoaded(true);});
    }
    if(tab==="challenges"&&!chLoaded){
      setChLoading(true);
      fbLoadChallenges().then(c=>{setChallenges(c);setChLoading(false);setChLoaded(true);});
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  },[tab]);

  // If discipline open — show DetailView
  if(activeDisc&&discObj){
    return(
      <>
        <SaveToast show={showSaved}/>
        <DetailView disc={discObj}
          dData={discData[activeDisc]||{records:[],subGoals:[],goalV:null}}
          uid={uid} profile={profile}
          onBack={()=>setActiveDisc(null)}
          onSaved={triggerSaved}
          onDiscDataChange={onDiscDataChange}/>
      </>
    );
  }

  // If challenge open
  if(openCh){
    return <ChallengeDetail challengeId={openCh} uid={uid} profile={profile}
      onBack={()=>{
        setOpenCh(null);
        fbLoadChallenges().then(c=>setChallenges(c));
      }}/>;
  }

  // ── Rankings helpers ──
  const rankDisc = ALL_DISCS.find(d=>d.id===discId);
  const rankC    = rankDisc?.catColor||"#FF6B35";
  const countries = ["all",...[...new Set(rankEntries.map(e=>e.country).filter(Boolean))].sort()];
  const filtered = rankEntries
    .filter(e=>e.bests?.[discId]!=null)
    .filter(e=>{
      if(ageF==="all") return true;
      const ag=AGE_GROUPS.find(a=>a.id===ageF);
      if(!ag||ag.id==="all") return true;
      const a=parseInt(e.age)||0; return a>=ag.min&&a<=ag.max;
    })
    .filter(e=>cntF==="all"||e.country===cntF)
    .sort((a,b)=>rankDisc?.lowerBetter?a.bests[discId]-b.bests[discId]:b.bests[discId]-a.bests[discId]);
  const myRank = filtered.findIndex(e=>e.id===uid)+1;
  const med = r=>r===1?"🥇":r===2?"🥈":r===3?"🥉":null;

  // ── Challenges helpers ──
  function chSorted(){
    const c=[...challenges];
    if(chTab==="popular") return c.sort((a,b)=>(b.participants||0)-(a.participants||0));
    if(chTab==="trending"){
      const week=Date.now()-7*24*60*60*1000;
      return c.filter(ch=>{
        const[d,m,y]=ch.createdAt?.split("/")||[];
        return new Date(`${y}-${m}-${d}`)>=week;
      }).sort((a,b)=>(b.participants||0)-(a.participants||0));
    }
    return c.sort((a,b)=>{
      const t=s=>{if(!s)return 0;const[d,m,y]=s.split("/");return new Date(`${y}-${m}-${d}`).getTime();};
      return t(b.createdAt)-t(a.createdAt);
    });
  }

  // ── Progress helpers ──
  const visCats = activeCat ? CATEGORIES.filter(c=>c.id===activeCat) : CATEGORIES;

  const tabs = [
    {id:"progress",  label:"My Progress", icon:"🏅"},
    {id:"rankings",  label:"Rankings",    icon:"🏆"},
    {id:"challenges",label:"Challenges",  icon:"🎯"},
  ];

  return(
    <div style={{minHeight:"100vh",background:"linear-gradient(160deg,#0d0d2b,#0f1e35)",
      color:"#fff",fontFamily:"'Archivo Black','Arial Black',sans-serif",maxWidth:480,margin:"0 auto"}}>
      <SaveToast show={showSaved}/>

      {/* ── Header ── */}
      <div style={{background:`linear-gradient(120deg,${profile.color},${profile.color}99)`,
        padding:"16px 16px 12px",position:"relative",overflow:"hidden"}}>
        <div style={{position:"absolute",top:-50,right:-30,width:200,height:200,
          background:"rgba(255,255,255,.07)",borderRadius:"50%",pointerEvents:"none"}}/>
        <div style={{display:"flex",alignItems:"center",gap:10}}>
          <button onClick={onShowProfile}
            style={{width:44,height:44,borderRadius:"50%",background:profile.color,
              display:"flex",alignItems:"center",justifyContent:"center",fontSize:20,
              flexShrink:0,border:"3px solid rgba(255,255,255,.3)",cursor:"pointer"}}>
            {profile.avatar}
          </button>
          <div style={{flex:1,minWidth:0,cursor:"pointer"}} onClick={onShowProfile}>
            <div style={{fontSize:19,fontWeight:600,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>
              {profile.name}
            </div>
            {profile.age&&<div style={{fontSize:14,color:"rgba(255,255,255,.8)",fontFamily:"Barlow,sans-serif",fontWeight:500}}>
              {profile.age} y.o. · {profile.country}
            </div>}
          </div>
          <button onClick={onShowHistory}
            style={{background:"rgba(0,0,0,.2)",borderRadius:10,padding:"8px 12px",
              color:"rgba(255,255,255,.65)",fontSize:13,minHeight:40,
              flexShrink:0,border:"none",cursor:"pointer"}} title="Training Log">
            📊
          </button>
          <button onClick={onSignOut}
            style={{background:"rgba(0,0,0,.2)",borderRadius:10,padding:"8px 12px",
              color:"rgba(255,255,255,.78)",fontFamily:"Barlow,sans-serif",fontWeight:600,
              fontSize:12,minHeight:40,flexShrink:0,border:"none",cursor:"pointer"}}>
            Sign out
          </button>
        </div>
      </div>

      {/* ── Tab bar ── */}
      <div style={{display:"flex",background:"rgba(255,255,255,.04)",
        borderBottom:"1px solid rgba(255,255,255,.08)"}}>
        {tabs.map(t=>{
          const active = tab===t.id;
          const colors = {progress:profile.color, rankings:"#FFD700", challenges:"#A78BFA"};
          const C2 = colors[t.id];
          return(
            <button key={t.id} onClick={()=>{setTab(t.id);logEvent("tab_view",{tab:t.id});}}
              style={{flex:1,minHeight:52,background:"transparent",border:"none",
                cursor:"pointer",display:"flex",flexDirection:"column",
                alignItems:"center",justifyContent:"center",gap:2,
                borderBottom:active?`3px solid ${C2}`:"3px solid transparent",
                transition:".2s"}}>
              <span style={{fontSize:18}}>{t.icon}</span>
              <span style={{fontSize:14,fontFamily:"Barlow,sans-serif",fontWeight:700,
                letterSpacing:.5,color:active?C2:"rgba(255,255,255,.55)"}}>
                {t.label.toUpperCase()}
              </span>
            </button>
          );
        })}
      </div>

      {/* ══════════════════════════════════════════
          TAB 1: MY PROGRESS
      ══════════════════════════════════════════ */}
      {tab==="progress"&&(
        <div>
          {/* Category filter */}
          <div style={{display:"flex",background:"rgba(255,255,255,.02)",
            overflowX:"auto",padding:"8px 14px 0",gap:6}}>
            {[{id:null,icon:"🏅",name:"All",color:profile.color},
              ...CATEGORIES.map(c=>({id:c.id,icon:c.icon,name:c.name,color:c.color}))
            ].map(c=>{
              const active=activeCat===c.id;
              return(
                <button key={String(c.id)} onClick={()=>setActiveCat(c.id)}
                  style={{minHeight:36,padding:"0 12px",whiteSpace:"nowrap",flexShrink:0,
                    background:active?`${c.color}22`:"rgba(255,255,255,.05)",
                    border:active?`1.5px solid ${c.color}66`:"1.5px solid rgba(255,255,255,.08)",
                    borderRadius:99,color:active?c.color:"rgba(255,255,255,.78)",
                    fontFamily:"Barlow,sans-serif",fontWeight:700,fontSize:14,
                    cursor:"pointer",transition:".15s"}}>
                  {c.icon} {c.name}
                </button>
              );
            })}
          </div>

          {/* Disciplines */}
          <div style={{padding:"12px 14px 50px"}}>
            <div style={{fontSize:13,letterSpacing:1.5,color:"rgba(255,255,255,.88)",fontFamily:"Barlow,sans-serif",fontWeight:600,marginBottom:14}}>
              TAP ANY DISCIPLINE TO TRACK RESULTS
            </div>
            {visCats.map(cat=>(
              <div key={cat.id} style={{background:"rgba(255,255,255,.04)",
                border:`1px solid ${cat.color}28`,borderRadius:20,padding:"14px",marginBottom:12}}>
                <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:10}}>
                  <span style={{fontSize:20}}>{cat.icon}</span>
                  <span style={{fontFamily:"'Archivo Black',sans-serif",fontSize:15,color:cat.color,fontWeight:900}}>
                    {cat.name.toUpperCase()}
                  </span>
                  <span style={{marginLeft:"auto",fontSize:13,fontFamily:"Barlow,sans-serif",color:"rgba(255,255,255,.65)",fontWeight:600}}>
                    {cat.disciplines.filter(d=>discData[d.id]?.records?.length>0).length}/{cat.disciplines.length}
                  </span>
                </div>
                <div style={{display:"flex",flexDirection:"column",gap:7}}>
                  {cat.disciplines.map(d=>{
                    const dd=discData[d.id]||{records:[],goalV:null};
                    const recs=dd.records||[];
                    const curV=bestVal(d,recs);
                    const goalV=dd.goalV;
                    const startV=recs.length?recs[0].v:null;
                    const p=calcPct(d,curV,goalV,startV);
                    const hasData=curV!=null||goalV!=null;
                    return(
                      <button key={d.id} onClick={()=>setActiveDisc(d.id)}
                        style={{display:"flex",alignItems:"center",gap:10,minHeight:56,
                          padding:"0 12px",
                          background:hasData?"rgba(255,255,255,.06)":"rgba(255,255,255,.02)",
                          border:hasData?`1px solid ${cat.color}44`:"1px solid rgba(255,255,255,.06)",
                          borderRadius:12,textAlign:"left",cursor:"pointer",transition:".15s"}}
                        onTouchStart={e=>e.currentTarget.style.background=`${cat.color}22`}
                        onTouchEnd={e=>e.currentTarget.style.background=hasData?"rgba(255,255,255,.06)":"rgba(255,255,255,.02)"}>
                        <div style={{width:76,fontFamily:"Barlow,sans-serif",fontWeight:700,
                          fontSize:15,color:cat.color,flexShrink:0}}>{d.name}</div>
                        <div style={{flex:1}}>
                          {hasData?(
                            <>
                              <div style={{display:"flex",justifyContent:"space-between",marginBottom:4}}>
                                <span style={{fontSize:19,fontFamily:"Barlow,sans-serif",fontWeight:700}}>
                                  {curV!=null?fmtVal(d.id,curV):"—"}
                                  <span style={{color:"rgba(255,255,255,.65)",fontSize:14,marginLeft:5,fontWeight:400}}>{d.unit}</span>
                                </span>
                                {goalV!=null&&<span style={{fontSize:14,fontFamily:"Barlow,sans-serif",color:"rgba(255,255,255,.85)",fontWeight:600}}>
                                  → {fmtVal(d.id,goalV)}
                                </span>}
                              </div>
                              {goalV!=null&&curV!=null&&(
                                <div style={{height:3,background:"rgba(255,255,255,.08)",borderRadius:99,overflow:"hidden"}}>
                                  <div style={{height:"100%",width:`${p}%`,background:cat.color,borderRadius:99}}/>
                                </div>
                              )}
                              {recs.length>0&&<div style={{fontSize:13,color:"rgba(255,255,255,.7)",fontFamily:"Barlow,sans-serif",fontWeight:600,marginTop:3}}>
                                {recs.length} result{recs.length!==1?"s":""}
                              </div>}
                            </>
                          ):(
                            <div style={{fontSize:15,fontFamily:"Barlow,sans-serif",color:`${cat.color}bb`,fontWeight:700}}>
                              Tap to add ＋
                            </div>
                          )}
                        </div>
                        <div style={{fontSize:18,opacity:.78,flexShrink:0,color:cat.color}}>›</div>
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ══════════════════════════════════════════
          TAB 2: RANKINGS
      ══════════════════════════════════════════ */}
      {tab==="rankings"&&(
        <div>
          {/* Discipline selector */}
          <div style={{padding:"10px 14px 0",overflowX:"auto"}}>
            <div style={{display:"flex",gap:6,paddingBottom:2,minWidth:"max-content"}}>
              {CATEGORIES.map(cat=>cat.disciplines.map(d=>(
                <button key={d.id} onClick={()=>setDiscId(d.id)}
                  style={{minHeight:38,padding:"0 14px",
                    background:discId===d.id?cat.color:"rgba(255,255,255,.07)",
                    color:discId===d.id?"#000":"rgba(255,255,255,.55)",
                    borderRadius:99,fontFamily:"Barlow,sans-serif",fontWeight:700,
                    fontSize:14,whiteSpace:"nowrap",border:"none",cursor:"pointer",transition:".2s"}}>
                  {d.name}
                </button>
              )))}
            </div>
          </div>

          {/* Age filter */}
          <div style={{padding:"8px 14px 0"}}>
            <div style={{display:"flex",gap:6,overflowX:"auto",paddingBottom:2}}>
              {AGE_GROUPS.map(a=>(
                <button key={a.id} onClick={()=>setAgeF(a.id)}
                  style={{minHeight:38,padding:"0 12px",whiteSpace:"nowrap",
                    background:ageF===a.id?"rgba(255,255,255,.15)":"rgba(255,255,255,.05)",
                    border:ageF===a.id?"1.5px solid rgba(255,255,255,.45)":"1.5px solid rgba(255,255,255,.08)",
                    borderRadius:99,color:ageF===a.id?"#fff":"rgba(255,255,255,.45)",
                    fontFamily:"Barlow,sans-serif",fontWeight:700,fontSize:14,
                    cursor:"pointer",transition:".15s"}}>
                  {a.label}
                </button>
              ))}
            </div>
          </div>

          {/* Country filter */}
          <div style={{padding:"6px 14px 0"}}>
            <div style={{display:"flex",gap:6,overflowX:"auto",paddingBottom:2}}>
              {countries.map(c=>(
                <button key={c} onClick={()=>setCntF(c)}
                  style={{minHeight:38,padding:"0 12px",whiteSpace:"nowrap",
                    background:cntF===c?"rgba(255,255,255,.15)":"rgba(255,255,255,.05)",
                    border:cntF===c?"1.5px solid rgba(255,255,255,.45)":"1.5px solid rgba(255,255,255,.08)",
                    borderRadius:99,color:cntF===c?"#fff":"rgba(255,255,255,.45)",
                    fontFamily:"Barlow,sans-serif",fontWeight:700,fontSize:14,
                    cursor:"pointer",transition:".15s"}}>
                  {c==="all"?"🌍 All":c}
                </button>
              ))}
            </div>
          </div>

          <div style={{padding:"12px 14px 50px"}}>
            {rankLoading&&<div style={{textAlign:"center",padding:"40px 0",opacity:.65,
              fontFamily:"Barlow,sans-serif",fontSize:14}} className="pl">Loading rankings…</div>}

            {!rankLoading&&filtered.length===0&&(
              <div style={{textAlign:"center",padding:"32px 20px"}}>
                <div style={{fontSize:40,marginBottom:12}}>🏜️</div>
                <div style={{fontSize:15,marginBottom:6}}>No results yet</div>
                <div style={{fontSize:13,opacity:.65,fontFamily:"Barlow,sans-serif",lineHeight:1.6}}>
                  Add a result for {rankDisc?.name} to appear here
                </div>
              </div>
            )}

            {!rankLoading&&filtered.length>0&&(
              <>
                {/* My rank badge */}
                {myRank>0&&(
                  <div style={{display:"flex",justifyContent:"center",marginBottom:16}}>
                    <div style={{background:`${rankC}22`,border:`1.5px solid ${rankC}55`,
                      borderRadius:99,padding:"8px 20px",display:"flex",alignItems:"center",gap:8}}>
                      <span style={{fontSize:15,fontFamily:"Barlow,sans-serif",color:"rgba(255,255,255,.75)",fontWeight:600}}>Your rank</span>
                      <span style={{fontSize:22,color:rankC,fontFamily:"'Archivo Black',sans-serif",fontWeight:900}}>
                        #{myRank}
                      </span>
                    </div>
                  </div>
                )}

                {/* Podium top 3 */}
                {filtered.length>=2&&(
                  <div style={{display:"flex",alignItems:"flex-end",justifyContent:"center",
                    gap:8,marginBottom:16}}>
                    {[1,0,2].filter(i=>i<filtered.length).map(i=>{
                      const e=filtered[i],rank=i+1;
                      const heights=[80,100,68];
                      const isMe=e.id===uid;
                      const podiumC=rank===1?"#FFD700":rank===2?"#C0C0C0":"#CD7F32";
                      return(
                        <div key={e.id} style={{display:"flex",flexDirection:"column",
                          alignItems:"center",flex:1,maxWidth:120}}>
                          <div style={{fontSize:20,marginBottom:3}}>{e.avatar}</div>
                          <div style={{fontSize:14,fontFamily:"Barlow,sans-serif",fontWeight:700,
                            textAlign:"center",width:"100%",overflow:"hidden",
                            textOverflow:"ellipsis",whiteSpace:"nowrap",
                            paddingInline:4,marginBottom:2}}>{e.name}</div>
                          <div style={{fontSize:13,color:rankC,marginBottom:6,
                            fontFamily:"'Archivo Black',sans-serif"}}>
                            {fmtVal(discId,e.bests[discId])}
                          </div>
                          <div style={{width:"100%",height:heights[i]||60,
                            background:isMe?`${e.color}88`:"rgba(255,255,255,.07)",
                            border:isMe?`2px solid ${e.color}`:`2px solid ${podiumC}55`,
                            borderRadius:"10px 10px 0 0",
                            display:"flex",alignItems:"center",justifyContent:"center",fontSize:22}}>
                            {med(rank)||`#${rank}`}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}

                {/* Full list */}
                {filtered.map((e,i)=>{
                  const rank=i+1,isMe=e.id===uid;
                  return(
                    <div key={e.id} style={{display:"flex",alignItems:"center",gap:10,
                      minHeight:60,padding:"0 12px",marginBottom:7,
                      background:isMe?`${e.color}18`:"rgba(255,255,255,.04)",
                      border:isMe?`1.5px solid ${e.color}55`:"1px solid rgba(255,255,255,.07)",
                      borderRadius:14}}>
                      <div style={{width:30,textAlign:"center",flexShrink:0}}>
                        {rank<=3?<span style={{fontSize:20}}>{med(rank)}</span>
                          :<span style={{fontSize:13,fontFamily:"Barlow,sans-serif",opacity:.78}}>#{rank}</span>}
                      </div>
                      <div style={{width:36,height:36,borderRadius:"50%",background:e.color,
                        display:"flex",alignItems:"center",justifyContent:"center",fontSize:17,
                        flexShrink:0,border:isMe?"2px solid #fff":"none"}}>{e.avatar}</div>
                      <div style={{flex:1,minWidth:0}}>
                        <div style={{display:"flex",alignItems:"center",gap:5}}>
                          <span style={{fontSize:16,fontWeight:600,overflow:"hidden",textOverflow:"ellipsis",
                            whiteSpace:"nowrap"}}>{e.name}</span>
                          {isMe&&<span style={{fontSize:11,background:e.color,color:"#000",
                            borderRadius:4,padding:"2px 6px",fontFamily:"Barlow,sans-serif",
                            fontWeight:700,flexShrink:0}}>YOU</span>}
                        </div>
                        <div style={{fontSize:13,color:"rgba(255,255,255,.65)",fontFamily:"Barlow,sans-serif",fontWeight:500,marginTop:2}}>
                          {[e.age&&`${e.age} y.o.`,e.country].filter(Boolean).join(" · ")}
                        </div>
                      </div>
                      <div style={{textAlign:"right",flexShrink:0}}>
                        <div style={{fontSize:18,color:rankC,fontWeight:700,fontFamily:"Barlow,sans-serif"}}>{fmtVal(discId,e.bests[discId])}</div>
                        <div style={{fontSize:13,color:"rgba(255,255,255,.88)",fontFamily:"Barlow,sans-serif"}}>{rankDisc?.unit}</div>
                      </div>
                    </div>
                  );
                })}
                <div style={{textAlign:"center",marginTop:12,fontSize:11,opacity:.82,
                  fontFamily:"Barlow,sans-serif"}}>
                  {filtered.length} athlete{filtered.length!==1?"s":""} · updates automatically
                </div>
              </>
            )}

            {/* ── Foxvelo user records ── */}
            <UserRecords uid={uid}/>

            {/* ── Exercise of the Month champions ── */}
            <MonthlyChampions uid={uid} onOpen={onShowMonthly}/>

            {/* ── Points Leaderboard ── */}
            <PointsLeaderboard uid={uid} profile={profile}/>
          </div>
        </div>
      )}

      {/* ══════════════════════════════════════════
          TAB 3: CHALLENGES
      ══════════════════════════════════════════ */}
      {tab==="challenges"&&(
        <div>
          {showCreate&&(
            <CreateChallengeModal uid={uid} profile={profile}
              onClose={()=>setShowCreate(false)}
              onCreated={()=>{
                setChLoaded(false);
                fbLoadChallenges().then(c=>{setChallenges(c);setChLoaded(true);});
              }}/>
          )}

          {/* Sub-tabs */}
          <div style={{display:"flex",background:"rgba(255,255,255,.03)",
            borderBottom:"1px solid rgba(255,255,255,.07)"}}>
            {[
              {id:"latest",label:"🕐 Latest"},
              {id:"popular",label:"🔥 Popular"},
              {id:"trending",label:"📈 Trending"},
            ].map(t=>(
              <button key={t.id} onClick={()=>setChTab(t.id)}
                style={{flex:1,minHeight:44,background:"transparent",border:"none",
                  cursor:"pointer",fontFamily:"Barlow,sans-serif",fontWeight:600,fontSize:12,
                  color:chTab===t.id?"#fff":"rgba(255,255,255,.4)",
                  borderBottom:chTab===t.id?"3px solid #A78BFA":"3px solid transparent",
                  transition:".2s"}}>
                {t.label}
              </button>
            ))}
          </div>

          <div style={{padding:"12px 14px 50px"}}>
            {/* New challenge button */}
            <button onClick={()=>setShowCreate(true)}
              style={{width:"100%",minHeight:52,marginBottom:14,
                background:"linear-gradient(90deg,rgba(167,139,250,.2),rgba(255,60,172,.1))",
                border:"1.5px dashed rgba(167,139,250,.5)",borderRadius:16,
                color:"#A78BFA",fontFamily:"'Archivo Black',sans-serif",
                fontSize:16,fontWeight:700,cursor:"pointer",display:"flex",
                alignItems:"center",justifyContent:"center",gap:10}}>
              <span style={{fontSize:22}}>＋</span> Create Challenge
            </button>

            {chLoading&&<div style={{textAlign:"center",padding:"32px 0",opacity:.65,
              fontFamily:"Barlow,sans-serif",fontSize:14}} className="pl">Loading…</div>}

            {!chLoading&&chSorted().length===0&&(
              <div style={{textAlign:"center",padding:"40px 20px"}}>
                <div style={{fontSize:40,marginBottom:12}}>🏆</div>
                <div style={{fontSize:15,marginBottom:6}}>
                  {chTab==="trending"?"No trending challenges this week":"No challenges yet"}
                </div>
                <div style={{fontSize:13,opacity:.65,fontFamily:"Barlow,sans-serif",lineHeight:1.6}}>
                  Be the first to create one!
                </div>
              </div>
            )}

            {!chLoading&&chSorted().map(ch=>{
              const myCount=(ch.entries||[]).filter(e=>e.uid===uid).length;
              const leader=(ch.entries||[])[0];
              return(
                <div key={ch.id}
                  style={{background:"rgba(255,255,255,.04)",
                    border:`1px solid ${myCount>0?"rgba(167,139,250,.4)":"rgba(255,255,255,.07)"}`,
                    borderRadius:16,padding:"14px",marginBottom:9,cursor:"pointer"}}
                  onClick={()=>setOpenCh(ch.id)}>

                  <div style={{display:"flex",alignItems:"flex-start",
                    justifyContent:"space-between",marginBottom:6}}>
                    <div style={{flex:1,minWidth:0}}>
                      <div style={{display:"flex",alignItems:"center",gap:6,marginBottom:4}}>
                        {myCount>0&&<span style={{fontSize:11,background:"#A78BFA",color:"#000",
                          borderRadius:4,padding:"1px 6px",fontFamily:"Barlow,sans-serif",
                          fontWeight:700}}>JOINED</span>}
                      </div>
                      <div style={{fontSize:17,lineHeight:1.3,paddingRight:8,fontWeight:700}}>{ch.title}</div>
                    </div>
                    <div style={{textAlign:"right",flexShrink:0}}>
                      <div style={{fontSize:14,color:"#A78BFA",fontFamily:"'Archivo Black',sans-serif"}}>
                        {ch.participants||0}
                      </div>
                      <div style={{fontSize:13,color:"rgba(255,255,255,.82)",fontFamily:"Barlow,sans-serif",fontWeight:600}}>athletes</div>
                    </div>
                  </div>

                  {ch.desc&&(
                    <div style={{fontSize:12,opacity:.65,fontFamily:"Barlow,sans-serif",
                      lineHeight:1.5,marginBottom:8,overflow:"hidden",textOverflow:"ellipsis",
                      display:"-webkit-box",WebkitLineClamp:1,WebkitBoxOrient:"vertical"}}>
                      {ch.desc}
                    </div>
                  )}

                  {leader&&(
                    <div style={{display:"flex",alignItems:"center",gap:8,
                      padding:"6px 10px",background:"rgba(255,255,255,.04)",borderRadius:8,marginBottom:8}}>
                      <span style={{fontSize:14}}>🥇</span>
                      <div style={{width:22,height:22,borderRadius:"50%",background:leader.color,
                        display:"flex",alignItems:"center",justifyContent:"center",fontSize:11}}>
                        {leader.avatar}
                      </div>
                      <span style={{fontSize:14,fontFamily:"Barlow,sans-serif",
                        color:"rgba(255,255,255,.65)",flex:1,overflow:"hidden",
                        textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{leader.name}</span>
                      {leader.resultText&&<span style={{fontSize:13,color:"#A78BFA",
                        fontFamily:"'Archivo Black',sans-serif"}}>{leader.resultText}</span>}
                    </div>
                  )}

                  <div style={{display:"flex",justifyContent:"space-between",alignItems:"center"}}>
                    <div style={{fontSize:13,color:"rgba(255,255,255,.82)",fontFamily:"Barlow,sans-serif",fontWeight:600}}>
                      {ch.createdAt}
                    </div>
                    <div style={{fontSize:12,color:"#A78BFA",fontFamily:"Barlow,sans-serif",fontWeight:600}}>
                      {myCount>0?"View →":"Join →"}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}


// ─── LOG TRAINING MODAL (no discipline, note required) ──────────────
function LogTrainingModal({uid, logs, onSave, onClose}){
  const [note, setNote]         = useState("");
  const [effort, setEffort]     = useState(3);
  const [saving, setSaving]     = useState(false);

  const effortLabels = ["","Easy","Moderate","Hard","Very Hard","Max Effort 🔥"];
  const effortColors = ["","#06D6A0","#FFD23F","#FF6B35","#FF3CAC","#A78BFA"];

  async function handleSave(){
    if(!note.trim()) return;
    setSaving(true);
    const newLog = {id: Date.now().toString(), date: TODAY(), note: note.trim(), effort};
    const updated = [...logs, newLog];
    logEvent("training_logged",{effort});
    await fbSaveTrainingLog(uid, updated);
    onSave(updated);
    setSaving(false);
    onClose();
  }

  return(
    <div
      style={{position:"fixed",inset:0,background:"rgba(0,0,0,.85)",zIndex:300,
        display:"flex",flexDirection:"column",justifyContent:"flex-end",
        backdropFilter:"blur(8px)",overscrollBehavior:"contain"}}
      onClick={onClose}
      onTouchMove={e=>e.stopPropagation()}>
      <div
        onClick={e=>e.stopPropagation()}
        onTouchMove={e=>e.stopPropagation()}
        style={{background:"linear-gradient(160deg,#12123a,#0f1e35)",
          borderRadius:"24px 24px 0 0",padding:"28px 20px 48px",
          border:"1px solid rgba(255,255,255,.1)",borderBottom:"none",
          maxHeight:"92vh",overflowY:"auto",
          overscrollBehavior:"contain"}}
        className="fi">

        {/* Drag handle */}
        <div style={{width:40,height:4,background:"rgba(255,255,255,.2)",
          borderRadius:99,margin:"0 auto 24px"}}/>

        <div style={{fontSize:20,marginBottom:4}}>📝 Log Training</div>
        <div style={{fontSize:13,opacity:.75,fontFamily:"Barlow,sans-serif",marginBottom:24}}>
          Record today's session — what did you do?
        </div>

        {/* Notes — main required field */}
        <div style={{fontSize:13,letterSpacing:2,color:"#FF6B35",
          fontFamily:"Barlow,sans-serif",marginBottom:8,fontWeight:600}}>
          TRAINING NOTES *
        </div>
        <textarea
          value={note} onChange={e=>setNote(e.target.value)}
          placeholder="e.g. 4×300m @ 3:20/km pace, felt strong. Windy conditions. 2×200m finish..."
          rows={4}
          style={{width:"100%",background:"rgba(255,255,255,.07)",
            border:`1.5px solid ${note.trim()?"#FF6B35":"rgba(255,255,255,.12)"}`,
            borderRadius:14,padding:"14px 16px",color:"#fff",
            fontFamily:"Barlow,sans-serif",fontSize:15,
            marginBottom:20,outline:"none",boxSizing:"border-box",
            resize:"none",lineHeight:1.6}}/>

        {/* Effort level */}
        <div style={{fontSize:13,letterSpacing:2,opacity:.78,
          fontFamily:"Barlow,sans-serif",marginBottom:10}}>
          EFFORT LEVEL
        </div>
        <div style={{display:"flex",gap:8,marginBottom:10}}>
          {[1,2,3,4,5].map(n=>(
            <button key={n} onClick={()=>setEffort(n)}
              style={{flex:1,minHeight:52,borderRadius:14,cursor:"pointer",
                background:effort>=n?`${effortColors[n]}33`:"rgba(255,255,255,.06)",
                border:effort===n?`2px solid ${effortColors[n]}`:"2px solid rgba(255,255,255,.08)",
                fontSize:20,transition:".15s",display:"flex",alignItems:"center",
                justifyContent:"center"}}>
              ⭐
            </button>
          ))}
        </div>
        <div style={{fontSize:13,fontFamily:"'Archivo Black',sans-serif",
          color:effortColors[effort],marginBottom:24,textAlign:"center",letterSpacing:.5}}>
          {effortLabels[effort]}
        </div>

        <button onClick={handleSave}
          disabled={saving||!note.trim()}
          style={{width:"100%",minHeight:56,
            background:note.trim()?"linear-gradient(90deg,#FF6B35,#FF3CAC)":"rgba(255,255,255,.08)",
            color:note.trim()?"#fff":"rgba(255,255,255,.3)",
            borderRadius:14,border:"none",cursor:note.trim()?"pointer":"default",
            fontFamily:"'Archivo Black',sans-serif",fontSize:16}}>
          {saving?"Saving…":"✓ Save Training"}
        </button>
      </div>
    </div>
  );
}

// ─── TRAINING HISTORY (general log) ─────────────────────────────────
function TrainingHistory({uid, profile, onBack}){
  const [logs, setLogs]       = useState([]);
  const [loading, setLoading] = useState(true);
  const [showLog, setShowLog] = useState(false);

  const effortColors = ["","#06D6A0","#FFD23F","#FF6B35","#FF3CAC","#A78BFA"];
  const effortLabels = ["","Easy","Moderate","Hard","Very Hard","Max Effort 🔥"];

  useEffect(()=>{
    fbGetTrainingLog(uid).then(l=>{setLogs(l);setLoading(false);});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  },[uid]);

  const sorted = [...logs].sort((a,b)=>{
    const toMs=s=>{if(!s)return 0;const[d,m,y]=s.split("/");return new Date(`${y}-${m}-${d}`).getTime();};
    return toMs(b.date)-toMs(a.date);
  });

  return(
    <div style={{minHeight:"100vh",background:"linear-gradient(160deg,#0d0d2b,#0f1e35)",
      color:"#fff",fontFamily:"'Archivo Black','Arial Black',sans-serif",
      maxWidth:480,margin:"0 auto"}}>

      {showLog&&(
        <LogTrainingModal uid={uid} logs={logs}
          onSave={updated=>setLogs(updated)}
          onClose={()=>setShowLog(false)}/>
      )}

      {/* Header */}
      <div style={{background:`linear-gradient(90deg,${profile.color},${profile.color}88)`,
        padding:"16px 16px 14px",display:"flex",alignItems:"center",gap:12,
        position:"relative",overflow:"hidden"}}>
        <div style={{position:"absolute",top:-40,right:-20,width:130,height:130,
          background:"rgba(255,255,255,.07)",borderRadius:"50%",pointerEvents:"none"}}/>
        <button onClick={onBack}
          style={{minHeight:46,background:"rgba(0,0,0,.25)",borderRadius:12,
            padding:"0 16px",color:"#fff",fontFamily:"'Archivo Black',sans-serif",
            fontSize:14,border:"none",cursor:"pointer",flexShrink:0}}>← Back</button>
        <div style={{flex:1}}>
          <div style={{fontSize:14,letterSpacing:1.5,opacity:.75,fontFamily:"Barlow,sans-serif",
            fontWeight:600}}>{profile.name.toUpperCase()}</div>
          <div style={{fontSize:20}}>📊 Training Log</div>
        </div>
        <div style={{fontSize:12,opacity:.78,fontFamily:"Barlow,sans-serif",flexShrink:0}}>
          {logs.length} session{logs.length!==1?"s":""}
        </div>
      </div>

      {/* Log button */}
      <div style={{padding:"14px 14px 0"}}>
        <button onClick={()=>setShowLog(true)}
          style={{width:"100%",minHeight:60,
            background:`linear-gradient(90deg,${profile.color}33,${profile.color}11)`,
            border:`1.5px dashed ${profile.color}77`,borderRadius:18,
            color:profile.color,fontFamily:"'Archivo Black',sans-serif",
            fontSize:16,cursor:"pointer",display:"flex",
            alignItems:"center",justifyContent:"center",gap:12,
            boxShadow:`0 4px 20px ${profile.color}22`}}>
          <span style={{fontSize:24}}>＋</span>
          Log Today's Training
        </button>
      </div>

      {/* Sessions */}
      <div style={{padding:"14px 14px 50px"}}>
        {loading&&(
          <div style={{textAlign:"center",padding:"40px",opacity:.65,
            fontFamily:"Barlow,sans-serif",fontSize:14}} className="pl">
            Loading…
          </div>
        )}

        {!loading&&sorted.length===0&&(
          <div style={{textAlign:"center",padding:"48px 20px"}}>
            <div style={{fontSize:48,marginBottom:16}}>🏃</div>
            <div style={{fontSize:16,marginBottom:8}}>No sessions yet</div>
            <div style={{fontSize:13,opacity:.65,fontFamily:"Barlow,sans-serif",lineHeight:1.6}}>
              Tap "Log Today's Training" to start
            </div>
          </div>
        )}

        {!loading&&sorted.map((log,i)=>{
          const C2 = effortColors[log.effort]||"#FF6B35";
          return(
            <div key={log.id||i} style={{background:"rgba(255,255,255,.04)",
              border:`1px solid ${C2}33`,borderRadius:18,padding:"16px",marginBottom:10}}>

              {/* Header row */}
              <div style={{display:"flex",alignItems:"center",
                justifyContent:"space-between",marginBottom:12}}>
                <div style={{display:"flex",alignItems:"center",gap:10}}>
                  <div style={{width:36,height:36,borderRadius:10,
                    background:`${C2}22`,border:`1px solid ${C2}44`,
                    display:"flex",alignItems:"center",justifyContent:"center",
                    fontSize:16}}>
                    💪
                  </div>
                  <div>
                    <div style={{fontSize:13,color:C2,fontFamily:"'Archivo Black',sans-serif"}}>
                      {effortLabels[log.effort]||"Training"}
                    </div>
                    <div style={{fontSize:11,opacity:.65,fontFamily:"Barlow,sans-serif",marginTop:1}}>
                      {log.date}
                    </div>
                  </div>
                </div>
                <div style={{display:"flex",gap:2}}>
                  {[1,2,3,4,5].map(n=>(
                    <span key={n} style={{fontSize:14,opacity:log.effort>=n?1:.2}}>⭐</span>
                  ))}
                </div>
              </div>

              {/* Note */}
              <div style={{fontSize:14,fontFamily:"Barlow,sans-serif",
                color:"rgba(255,255,255,.75)",lineHeight:1.6,
                borderLeft:`3px solid ${C2}55`,paddingLeft:12}}>
                {log.note}
              </div>
            </div>
          );
        })}

        {/* Stats */}
        {logs.length>0&&(
          <div style={{background:"rgba(255,255,255,.03)",
            border:"1px solid rgba(255,255,255,.07)",
            borderRadius:16,padding:"16px",marginTop:6}}>
            <div style={{fontSize:14,letterSpacing:1.5,opacity:.65,
              fontFamily:"Barlow,sans-serif",marginBottom:12}}>STATS</div>
            <div style={{display:"flex",gap:0}}>
              {[
                {label:"Total",val:logs.length},
                {label:"This month",val:logs.filter(l=>{
                  const now=new Date();
                  const[,m,y]=l.date?.split("/")||[];
                  return +m===now.getMonth()+1&&+y===now.getFullYear();
                }).length},
                {label:"Avg effort",val:logs.length?
                  (logs.reduce((s,l)=>s+(l.effort||3),0)/logs.length).toFixed(1):"—"},
              ].map((s,i)=>(
                <div key={i} style={{flex:1,textAlign:"center",
                  borderRight:i<2?"1px solid rgba(255,255,255,.07)":"none"}}>
                  <div style={{fontSize:22,color:profile.color}}>{s.val}</div>
                  <div style={{fontSize:11,opacity:.75,fontFamily:"Barlow,sans-serif",
                    marginTop:2,letterSpacing:.5}}>{s.label.toUpperCase()}</div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── CREATE CHALLENGE MODAL (free form, no discipline) ──────────────
function CreateChallengeModal({uid, profile, onClose, onCreated}){
  const [title, setTitle]   = useState("");
  const [desc, setDesc]     = useState("");
  const [saving, setSaving] = useState(false);

  async function create(){
    if(!title.trim()) return;
    setSaving(true);
    logEvent("challenge_created");
    const id = await fbCreateChallenge({
      title:title.trim(), desc:desc.trim(),
      creatorUid:uid, creatorName:profile.name,
      creatorAvatar:profile.avatar, creatorColor:profile.color,
    });
    setSaving(false);
    onCreated(id);
    onClose();
  }

  return(
    <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,.88)",zIndex:300,
      display:"flex",flexDirection:"column",justifyContent:"flex-end",
      backdropFilter:"blur(8px)",overscrollBehavior:"contain"}}
      onClick={onClose}
      onTouchMove={e=>e.stopPropagation()}>
      <div onClick={e=>e.stopPropagation()}
        onTouchMove={e=>e.stopPropagation()}
        style={{background:"linear-gradient(160deg,#12123a,#0f1e35)",
          borderRadius:"24px 24px 0 0",padding:"28px 20px 48px",
          border:"1px solid rgba(255,255,255,.1)",borderBottom:"none",
          maxHeight:"90vh",overflowY:"auto",overscrollBehavior:"contain"}}
        className="fi">

        <div style={{width:40,height:4,background:"rgba(255,255,255,.2)",
          borderRadius:99,margin:"0 auto 24px"}}/>

        <div style={{fontSize:20,marginBottom:4}}>🏆 Create Challenge</div>
        <div style={{fontSize:13,opacity:.75,fontFamily:"Barlow,sans-serif",marginBottom:24}}>
          Set a challenge others can join and complete
        </div>

        <div style={{fontSize:13,letterSpacing:2,color:"#A78BFA",
          fontFamily:"Barlow,sans-serif",marginBottom:8,fontWeight:600}}>
          CHALLENGE TITLE *
        </div>
        <input value={title} onChange={e=>setTitle(e.target.value)}
          onKeyDown={e=>e.key==="Enter"&&create()}
          placeholder="e.g. Run 5km under 25 minutes"
          style={{width:"100%",minHeight:56,background:"rgba(255,255,255,.07)",
            border:`1.5px solid ${title?"#A78BFA":"rgba(255,255,255,.1)"}`,
            borderRadius:14,padding:"0 16px",color:"#fff",fontFamily:"Barlow,sans-serif",
            fontSize:16,marginBottom:20,outline:"none",boxSizing:"border-box"}}/>

        <div style={{fontSize:13,letterSpacing:2,opacity:.78,
          fontFamily:"Barlow,sans-serif",marginBottom:8}}>
          DESCRIPTION (optional)
        </div>
        <textarea value={desc} onChange={e=>setDesc(e.target.value)}
          placeholder="What's this challenge about? Rules, conditions, time frame..."
          rows={3}
          style={{width:"100%",background:"rgba(255,255,255,.06)",
            border:"1px solid rgba(255,255,255,.1)",borderRadius:12,
            padding:"14px 16px",color:"#fff",fontFamily:"Barlow,sans-serif",
            fontSize:15,marginBottom:24,outline:"none",
            boxSizing:"border-box",resize:"none",lineHeight:1.6}}/>

        <button onClick={create} disabled={saving||!title.trim()}
          style={{width:"100%",minHeight:56,
            background:title.trim()?"linear-gradient(90deg,#A78BFA,#FF3CAC)":"rgba(255,255,255,.08)",
            color:title.trim()?"#fff":"rgba(255,255,255,.3)",
            borderRadius:14,border:"none",cursor:title.trim()?"pointer":"default",
            fontFamily:"'Archivo Black',sans-serif",fontSize:16}}>
          {saving?"Creating…":"🏆 Create Challenge"}
        </button>
      </div>
    </div>
  );
}

// ─── JOIN CHALLENGE MODAL (note required, optional result text) ──────
function JoinChallengeModal({challenge, uid, profile, onClose, onJoined}){
  const [note, setNote]         = useState("");
  const [resultText, setResult] = useState("");
  const [saving, setSaving]     = useState(false);

  async function join(){
    if(!note.trim()) return;
    setSaving(true);
    logEvent("challenge_joined",{challenge_id:challenge.id});
    await fbJoinChallenge(challenge.id, uid, profile, note.trim(), resultText.trim());
    setSaving(false);
    onJoined();
    onClose();
  }

  return(
    <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,.88)",zIndex:300,
      display:"flex",flexDirection:"column",justifyContent:"flex-end",
      backdropFilter:"blur(8px)",overscrollBehavior:"contain"}}
      onClick={onClose}
      onTouchMove={e=>e.stopPropagation()}>
      <div onClick={e=>e.stopPropagation()}
        onTouchMove={e=>e.stopPropagation()}
        style={{background:"linear-gradient(160deg,#12123a,#0f1e35)",
          borderRadius:"24px 24px 0 0",padding:"28px 20px 48px",
          border:"1px solid rgba(255,255,255,.1)",borderBottom:"none",
          maxHeight:"90vh",overflowY:"auto",overscrollBehavior:"contain"}}
        className="fi">

        <div style={{width:40,height:4,background:"rgba(255,255,255,.2)",
          borderRadius:99,margin:"0 auto 24px"}}/>

        <div style={{fontSize:18,marginBottom:4}}>{challenge.title}</div>
        {challenge.desc&&(
          <div style={{fontSize:13,opacity:.75,fontFamily:"Barlow,sans-serif",
            marginBottom:20,lineHeight:1.5}}>{challenge.desc}</div>
        )}

        <div style={{fontSize:13,letterSpacing:2,color:"#FF6B35",
          fontFamily:"Barlow,sans-serif",marginBottom:8,fontWeight:600}}>
          YOUR RESULT / NOTES *
        </div>
        <textarea value={note} onChange={e=>setNote(e.target.value)}
          placeholder="What did you do? How did it go? Describe your attempt..."
          rows={4}
          style={{width:"100%",background:"rgba(255,255,255,.07)",
            border:`1.5px solid ${note.trim()?"#FF6B35":"rgba(255,255,255,.12)"}`,
            borderRadius:14,padding:"14px 16px",color:"#fff",
            fontFamily:"Barlow,sans-serif",fontSize:15,
            marginBottom:16,outline:"none",boxSizing:"border-box",
            resize:"none",lineHeight:1.6}}/>

        <div style={{fontSize:13,letterSpacing:2,opacity:.78,
          fontFamily:"Barlow,sans-serif",marginBottom:8}}>
          RESULT (optional, e.g. "24:35" or "3.50m")
        </div>
        <input value={resultText} onChange={e=>setResult(e.target.value)}
          placeholder="Optional numeric result"
          style={{width:"100%",minHeight:52,background:"rgba(255,255,255,.06)",
            border:"1px solid rgba(255,255,255,.1)",borderRadius:12,
            padding:"0 16px",color:"#fff",fontFamily:"'Archivo Black',sans-serif",
            fontSize:18,marginBottom:24,outline:"none",
            boxSizing:"border-box",letterSpacing:1}}/>

        <button onClick={join} disabled={saving||!note.trim()}
          style={{width:"100%",minHeight:56,
            background:note.trim()?"linear-gradient(90deg,#FF6B35,#FF3CAC)":"rgba(255,255,255,.08)",
            color:note.trim()?"#fff":"rgba(255,255,255,.3)",
            borderRadius:14,border:"none",
            cursor:note.trim()?"pointer":"default",
            fontFamily:"'Archivo Black',sans-serif",fontSize:16}}>
          {saving?"Saving…":"✓ Submit Result"}
        </button>
      </div>
    </div>
  );
}

// ─── CHALLENGE SHARE CARD (free form) ───────────────────────────────
function ChallengeShareCard({challenge, myEntry, onClose}){
  const entries = [...(challenge?.entries||[])];
  const myRank  = myEntry ? entries.findIndex(
    e=>e.uid===myEntry.uid&&e.note===myEntry.note&&e.date===myEntry.date
  )+1 : 0;

  const shareUrl = `${window.location.origin}?challenge=${challenge?.id}`;

  async function share(){
    logEvent("share",{content_type:"challenge"});
    const text = myEntry
      ? `🏆 ${challenge.title}\n${myEntry.name}${myEntry.resultText?" — "+myEntry.resultText:""}\n${myEntry.note}\n\nJoin: ${shareUrl}`
      : `🏆 ${challenge.title}\n${entries.length} athletes competing\n\n${shareUrl}`;
    if(navigator.share){
      try{await navigator.share({title:"Foxvelo Challenge",text,url:shareUrl});return;}
      catch(e){if(e.name==="AbortError")return;}
    }
    navigator.clipboard?.writeText(shareUrl).then(()=>alert("Link copied!"));
  }

  return(
    <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,.92)",zIndex:200,
      display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",
      padding:"16px",overflowY:"auto"}} onClick={onClose}>

      <div onClick={e=>e.stopPropagation()}
        style={{width:"100%",maxWidth:360,
          background:"linear-gradient(145deg,#080820,#0d0d30,#120a28)",
          borderRadius:28,overflow:"hidden",
          boxShadow:"0 0 0 1px #A78BFA44, 0 32px 80px rgba(0,0,0,.8), 0 0 80px #A78BFA22",
          marginBottom:16}}>

        <div style={{height:4,background:"linear-gradient(90deg,#A78BFA,#FF3CAC,transparent)"}}/>

        <div style={{padding:"24px 24px 0"}}>
          <div style={{fontSize:14,letterSpacing:2,color:"#A78BFA",
            fontFamily:"'Archivo Black',sans-serif",marginBottom:8,opacity:.8}}>
            🏆 CHALLENGE
          </div>
          <div style={{fontSize:22,color:"#fff",fontFamily:"'Archivo Black',sans-serif",
            lineHeight:1.2,marginBottom:8}}>{challenge?.title}</div>
          {challenge?.desc&&(
            <div style={{fontSize:13,opacity:.65,fontFamily:"Barlow,sans-serif",
              marginBottom:16,lineHeight:1.5}}>{challenge.desc}</div>
          )}
          <div style={{fontSize:12,opacity:.65,fontFamily:"Barlow,sans-serif",marginBottom:20}}>
            {entries.length} athlete{entries.length!==1?"s":""} competing · {challenge?.createdAt}
          </div>
        </div>

        {myEntry&&(
          <div style={{margin:"0 24px 20px",textAlign:"center",
            padding:"20px",background:"rgba(167,139,250,.12)",borderRadius:16,
            border:"1px solid rgba(167,139,250,.3)"}}>
            <div style={{display:"flex",alignItems:"center",justifyContent:"center",
              gap:10,marginBottom:12}}>
              <div style={{width:36,height:36,borderRadius:"50%",background:myEntry.color,
                display:"flex",alignItems:"center",justifyContent:"center",fontSize:18}}>
                {myEntry.avatar}
              </div>
              <div style={{textAlign:"left"}}>
                <div style={{fontSize:14,color:"#fff",fontFamily:"'Archivo Black',sans-serif"}}>
                  {myEntry.name}
                </div>
                {myRank>0&&<div style={{fontSize:11,color:"#A78BFA",fontFamily:"Barlow,sans-serif"}}>
                  Entry #{myRank}
                </div>}
              </div>
            </div>
            {myEntry.resultText&&(
              <div style={{fontSize:48,color:"#A78BFA",fontFamily:"'Archivo Black',sans-serif",
                lineHeight:1,marginBottom:8,textShadow:"0 0 40px #A78BFA66"}}>
                {myEntry.resultText}
              </div>
            )}
            <div style={{fontSize:13,fontFamily:"Barlow,sans-serif",
              color:"rgba(255,255,255,.88)",fontStyle:"italic",lineHeight:1.5}}>
              "{myEntry.note}"
            </div>
          </div>
        )}

        {entries.length>0&&(
          <div style={{margin:"0 24px 20px"}}>
            <div style={{fontSize:14,letterSpacing:1.5,opacity:.6,
              fontFamily:"Barlow,sans-serif",marginBottom:8}}>PARTICIPANTS</div>
            {entries.slice(0,3).map((e,i)=>(
              <div key={i} style={{display:"flex",alignItems:"center",gap:10,
                minHeight:44,padding:"0 12px",marginBottom:6,
                background:"rgba(255,255,255,.04)",
                border:"1px solid rgba(255,255,255,.06)",borderRadius:10}}>
                <span style={{fontSize:18}}>{"🥇🥈🥉"[i]}</span>
                <div style={{width:28,height:28,borderRadius:"50%",background:e.color,
                  display:"flex",alignItems:"center",justifyContent:"center",fontSize:14}}>
                  {e.avatar}
                </div>
                <div style={{flex:1,minWidth:0}}>
                  <div style={{fontSize:13,fontFamily:"Barlow,sans-serif",
                    color:"rgba(255,255,255,.8)",overflow:"hidden",
                    textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{e.name}</div>
                  {e.resultText&&<div style={{fontSize:11,color:"#A78BFA",
                    fontFamily:"'Archivo Black',sans-serif"}}>{e.resultText}</div>}
                </div>
                <div style={{fontSize:11,opacity:.65,fontFamily:"Barlow,sans-serif",
                  flexShrink:0}}>{e.date}</div>
              </div>
            ))}
          </div>
        )}

        <div style={{borderTop:"1px solid rgba(255,255,255,.07)",
          padding:"16px 24px",display:"flex",alignItems:"center",justifyContent:"space-between"}}>
          <div style={{display:"flex",alignItems:"center",gap:8}}>
            <span style={{fontSize:18}}>🏅</span>
            <div>
              <div style={{fontSize:12,color:"rgba(255,255,255,.7)",
                fontFamily:"'Archivo Black',sans-serif"}}>FOXVELO</div>
              <div style={{fontSize:13,color:"rgba(255,255,255,.82)",fontWeight:600,
                fontFamily:"Barlow,sans-serif"}}>foxvelo.com</div>
            </div>
          </div>
          <div style={{fontSize:11,color:"#A78BFA",fontFamily:"Barlow,sans-serif",
            fontWeight:600,textAlign:"right"}}>Join the<br/>challenge →</div>
        </div>
      </div>

      <div onClick={e=>e.stopPropagation()}
        style={{width:"100%",maxWidth:360,display:"flex",gap:10}}>
        <button onClick={share}
          style={{flex:1,minHeight:52,
            background:"linear-gradient(90deg,#A78BFA,#FF3CAC)",
            color:"#fff",borderRadius:14,border:"none",cursor:"pointer",
            fontFamily:"'Archivo Black',sans-serif",fontSize:15,
            display:"flex",alignItems:"center",justifyContent:"center",gap:8}}>
          📤 Share Challenge
        </button>
        <button onClick={onClose}
          style={{minWidth:52,minHeight:52,background:"rgba(255,255,255,.1)",
            borderRadius:14,border:"none",cursor:"pointer",
            color:"rgba(255,255,255,.88)",fontFamily:"Barlow,sans-serif",fontWeight:600,fontSize:16}}>✕</button>
      </div>
      <div onClick={e=>e.stopPropagation()}
        style={{marginTop:10,fontSize:11,opacity:.82,fontFamily:"Barlow,sans-serif",textAlign:"center"}}>
        Screenshot for Instagram Stories
      </div>
    </div>
  );
}

// ─── CHALLENGE DETAIL ───────────────────────────────────────────────
function ChallengeDetail({challengeId, uid, profile, onBack}){
  const [challenge,setChallenge] = useState(null);
  const [loading,setLoading]     = useState(true);
  const [showJoin,setShowJoin]   = useState(false);
  const [showShare,setShowShare] = useState(null);

  useEffect(()=>{
    fbGetChallenge(challengeId).then(c=>{setChallenge(c);setLoading(false);});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  },[challengeId]);

  if(loading)return(
    <div style={{minHeight:"100vh",background:"linear-gradient(160deg,#0d0d2b,#0f1e35)",
      display:"flex",alignItems:"center",justifyContent:"center"}}>
      <div style={{textAlign:"center"}}>
        <div style={{fontSize:44,marginBottom:12}}>🏆</div>
        <div style={{color:"rgba(255,255,255,.72)",fontFamily:"Barlow,sans-serif",fontSize:14}} className="pl">Loading…</div>
      </div>
    </div>
  );
  if(!challenge)return(
    <div style={{minHeight:"100vh",background:"linear-gradient(160deg,#0d0d2b,#0f1e35)",
      display:"flex",alignItems:"center",justifyContent:"center"}}>
      <div style={{fontSize:14,opacity:.65,fontFamily:"Barlow,sans-serif"}}>Challenge not found</div>
    </div>
  );

  const entries = challenge.entries||[];
  const myEntries = entries.filter(e=>e.uid===uid);

  function reload(){
    setLoading(true);
    fbGetChallenge(challengeId).then(c=>{setChallenge(c);setLoading(false);});
  }

  return(
    <div style={{minHeight:"100vh",background:"linear-gradient(160deg,#0d0d2b,#0f1e35)",
      color:"#fff",fontFamily:"'Archivo Black','Arial Black',sans-serif",maxWidth:480,margin:"0 auto"}}>

      {showJoin&&(
        <JoinChallengeModal challenge={challenge} uid={uid} profile={profile}
          onClose={()=>setShowJoin(false)} onJoined={reload}/>
      )}
      {showShare&&(
        <ChallengeShareCard challenge={challenge} myEntry={showShare}
          onClose={()=>setShowShare(null)}/>
      )}

      {/* Header */}
      <div style={{background:"linear-gradient(90deg,#A78BFAcc,#FF3CAC44)",
        padding:"16px 16px 14px",position:"relative",overflow:"hidden"}}>
        <div style={{position:"absolute",top:-40,right:-20,width:140,height:140,
          background:"rgba(255,255,255,.07)",borderRadius:"50%",pointerEvents:"none"}}/>
        <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:8}}>
          <button onClick={onBack}
            style={{minHeight:44,background:"rgba(0,0,0,.25)",borderRadius:10,
              padding:"0 14px",color:"#fff",fontFamily:"'Archivo Black',sans-serif",
              fontSize:13,border:"none",cursor:"pointer",flexShrink:0}}>← Back</button>
          <div style={{flex:1}}>
            <div style={{fontSize:14,letterSpacing:1.5,opacity:.7,fontFamily:"Barlow,sans-serif",fontWeight:600}}>
              🏆 CHALLENGE
            </div>
            <div style={{fontSize:18,lineHeight:1.2}}>{challenge.title}</div>
          </div>
        </div>
        <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
          <div style={{background:"rgba(0,0,0,.2)",borderRadius:99,padding:"4px 12px",
            fontSize:14,fontFamily:"Barlow,sans-serif",color:"rgba(255,255,255,.7)"}}>
            👥 {entries.length} athlete{entries.length!==1?"s":""}
          </div>
          <div style={{background:"rgba(0,0,0,.2)",borderRadius:99,padding:"4px 12px",
            fontSize:14,fontFamily:"Barlow,sans-serif",color:"rgba(255,255,255,.82)"}}>
            by {challenge.creatorName}
          </div>
        </div>
      </div>

      <div style={{padding:"14px 14px 50px"}}>
        {challenge.desc&&(
          <div style={{fontSize:13,fontFamily:"Barlow,sans-serif",
            color:"rgba(255,255,255,.82)",marginBottom:14,lineHeight:1.6,
            padding:"12px 16px",background:"rgba(255,255,255,.04)",borderRadius:12}}>
            {challenge.desc}
          </div>
        )}

        <div style={{display:"flex",gap:8,marginBottom:16}}>
          <button onClick={()=>setShowJoin(true)}
            style={{flex:1,minHeight:52,
              background:"linear-gradient(90deg,#A78BFA,#FF3CAC)",
              color:"#fff",borderRadius:14,border:"none",cursor:"pointer",
              fontFamily:"'Archivo Black',sans-serif",fontSize:15}}>
            {myEntries.length>0?"＋ Add Another Result":"🏃 Join Challenge"}
          </button>
          <button onClick={()=>setShowShare(myEntries[0]||null)}
            style={{minWidth:52,minHeight:52,background:"rgba(255,255,255,.08)",
              borderRadius:14,border:"1px solid rgba(255,255,255,.15)",
              color:"rgba(255,255,255,.7)",fontSize:20,cursor:"pointer"}}>
            📤
          </button>
        </div>

        {myEntries.length>0&&(
          <div style={{background:"rgba(167,139,250,.12)",border:"1px solid rgba(167,139,250,.3)",
            borderRadius:14,padding:"14px 16px",marginBottom:16}}>
            <div style={{fontSize:11,opacity:.78,fontFamily:"Barlow,sans-serif",marginBottom:6}}>
              YOUR LATEST ENTRY
            </div>
            {myEntries[myEntries.length-1].resultText&&(
              <div style={{fontSize:24,color:"#A78BFA",fontFamily:"'Archivo Black',sans-serif",marginBottom:4}}>
                {myEntries[myEntries.length-1].resultText}
              </div>
            )}
            <div style={{fontSize:13,fontFamily:"Barlow,sans-serif",
              color:"rgba(255,255,255,.65)",fontStyle:"italic"}}>
              "{myEntries[myEntries.length-1].note}"
            </div>
          </div>
        )}

        <div style={{fontSize:14,letterSpacing:1.5,opacity:.65,
          fontFamily:"Barlow,sans-serif",marginBottom:10}}>
          ALL ENTRIES
        </div>
        {entries.length===0&&(
          <div style={{textAlign:"center",padding:"32px",opacity:.65,
            fontFamily:"Barlow,sans-serif",fontSize:14}}>
            No entries yet. Be the first!
          </div>
        )}
        {[...entries].reverse().map((e,i)=>{
          const isMe=e.uid===uid;
          return(
            <div key={i} style={{display:"flex",alignItems:"flex-start",gap:12,
              padding:"14px",marginBottom:8,
              background:isMe?"rgba(167,139,250,.1)":"rgba(255,255,255,.04)",
              border:isMe?"1.5px solid rgba(167,139,250,.4)":"1px solid rgba(255,255,255,.07)",
              borderRadius:14}}>
              <div style={{width:38,height:38,borderRadius:"50%",background:e.color,
                display:"flex",alignItems:"center",justifyContent:"center",
                fontSize:18,flexShrink:0,border:isMe?"2px solid #A78BFA":"none"}}>
                {e.avatar}
              </div>
              <div style={{flex:1,minWidth:0}}>
                <div style={{display:"flex",alignItems:"center",gap:6,marginBottom:4}}>
                  <span style={{fontSize:14,overflow:"hidden",textOverflow:"ellipsis",
                    whiteSpace:"nowrap"}}>{e.name}</span>
                  {isMe&&<span style={{fontSize:11,background:"#A78BFA",color:"#000",
                    borderRadius:4,padding:"1px 5px",fontFamily:"Barlow,sans-serif",
                    fontWeight:700,flexShrink:0}}>YOU</span>}
                  {e.resultText&&<span style={{fontSize:13,color:"#A78BFA",
                    fontFamily:"'Archivo Black',sans-serif",marginLeft:"auto",flexShrink:0}}>
                    {e.resultText}
                  </span>}
                </div>
                <div style={{fontSize:13,fontFamily:"Barlow,sans-serif",
                  color:"rgba(255,255,255,.88)",lineHeight:1.5,fontStyle:"italic"}}>
                  "{e.note}"
                </div>
                <div style={{fontSize:11,opacity:.82,fontFamily:"Barlow,sans-serif",marginTop:4}}>
                  {e.date}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}


// ─── POINTS LEADERBOARD ────────────────────────────────────────────
function PointsLeaderboard({uid, profile}){
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(()=>{
    fbLoadPointsLb().then(e=>{setEntries(e);setLoading(false);});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  },[]);

  const C = "#FFD700";
  const myRank = entries.findIndex(e=>e.id===uid)+1;
  const med = r=>r===1?"🥇":r===2?"🥈":r===3?"🥉":null;

  return(
    <div style={{background:"rgba(255,255,255,.04)",
      border:"1px solid rgba(255,255,255,.08)",
      borderRadius:18,padding:"16px",marginTop:16}}>
      <div style={{fontSize:14,letterSpacing:1.5,color:C,
        fontFamily:"Barlow,sans-serif",fontWeight:700,marginBottom:4}}>
        ⭐ POINTS LEADERBOARD
      </div>
      <div style={{fontSize:13,color:"rgba(255,255,255,.65)",
        fontFamily:"Barlow,sans-serif",marginBottom:14}}>
        Total activity points across all disciplines
      </div>

      {loading&&<div style={{textAlign:"center",padding:"20px",opacity:.5,
        fontFamily:"Barlow,sans-serif",fontSize:14}} className="pl">Loading…</div>}

      {!loading&&myRank>0&&(
        <div style={{display:"flex",justifyContent:"center",marginBottom:12}}>
          <div style={{background:`${C}22`,border:`1.5px solid ${C}55`,
            borderRadius:99,padding:"6px 18px",display:"flex",alignItems:"center",gap:8}}>
            <span style={{fontSize:14,color:"rgba(255,255,255,.75)",
              fontFamily:"Barlow,sans-serif",fontWeight:600}}>Your rank</span>
            <span style={{fontSize:20,color:C,fontFamily:"'Archivo Black',sans-serif",
              fontWeight:900}}>#{myRank}</span>
          </div>
        </div>
      )}

      {!loading&&entries.slice(0,10).map((e,i)=>{
        const rank=i+1, isMe=e.id===uid;
        return(
          <div key={e.id} style={{display:"flex",alignItems:"center",gap:10,
            minHeight:56,padding:"0 12px",marginBottom:6,
            background:isMe?`${e.color||profile.color}18`:"rgba(255,255,255,.04)",
            border:isMe?`1.5px solid ${e.color||profile.color}55`:"1px solid rgba(255,255,255,.07)",
            borderRadius:14}}>
            <div style={{width:30,textAlign:"center",flexShrink:0}}>
              {rank<=3?<span style={{fontSize:20}}>{med(rank)}</span>
                :<span style={{fontSize:14,fontFamily:"Barlow,sans-serif",
                  color:"rgba(255,255,255,.65)",fontWeight:600}}>#{rank}</span>}
            </div>
            <div style={{width:36,height:36,borderRadius:"50%",
              background:e.color||"#FF6B35",
              display:"flex",alignItems:"center",justifyContent:"center",
              fontSize:18,flexShrink:0,
              border:isMe?"2px solid #fff":"none"}}>{e.avatar||"🏃"}</div>
            <div style={{flex:1,minWidth:0}}>
              <div style={{display:"flex",alignItems:"center",gap:6}}>
                <span style={{fontSize:16,fontWeight:600,overflow:"hidden",
                  textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{e.name}</span>
                {isMe&&<span style={{fontSize:11,background:e.color||profile.color,
                  color:"#000",borderRadius:4,padding:"2px 6px",
                  fontFamily:"Barlow,sans-serif",fontWeight:700,flexShrink:0}}>YOU</span>}
              </div>
              <div style={{fontSize:13,color:"rgba(255,255,255,.65)",
                fontFamily:"Barlow,sans-serif",fontWeight:500,marginTop:1}}>
                {[e.age&&`${e.age} y.o.`,e.country].filter(Boolean).join(" · ")}
              </div>
            </div>
            <div style={{textAlign:"right",flexShrink:0}}>
              <div style={{fontSize:20,color:C,fontWeight:700,
                fontFamily:"Barlow,sans-serif"}}>{e.totalPoints||0}</div>
              <div style={{fontSize:12,color:"rgba(255,255,255,.6)",
                fontFamily:"Barlow,sans-serif"}}>pts</div>
            </div>
          </div>
        );
      })}

      {!loading&&entries.length===0&&(
        <div style={{textAlign:"center",padding:"20px",
          color:"rgba(255,255,255,.55)",fontFamily:"Barlow,sans-serif",fontSize:14}}>
          No points yet — start tracking your results!
        </div>
      )}
    </div>
  );
}

// ─── POINTS STATS BLOCK ─────────────────────────────────────────────
// eslint-disable-next-line no-unused-vars
function PointsStats({discData, logs, challengesJoinedCount, color}){
  const pts = calcUserPoints(discData, logs, challengesJoinedCount);
  const stats = [
    {label:"Results",    val:pts.results},
    {label:"Trainings",  val:pts.trainings},
    {label:"Challenges", val:pts.challengesJoined},
    {label:"Goals Set",  val:pts.goalsSet},
    {label:"Goals Done", val:pts.goalsAchieved},
  ];
  return(
    <div style={{background:"rgba(255,255,255,.04)",
      border:`1.5px solid ${color}44`,borderRadius:20,padding:"18px",marginBottom:14}}>
      <div style={{textAlign:"center",marginBottom:16,
        paddingBottom:14,borderBottom:"1px solid rgba(255,255,255,.08)"}}>
        <div style={{fontSize:14,letterSpacing:1.5,color:"rgba(255,255,255,.72)",
          fontFamily:"Barlow,sans-serif",fontWeight:600,marginBottom:6}}>TOTAL POINTS</div>
        <div style={{fontSize:52,fontWeight:900,color,lineHeight:1,
          textShadow:`0 0 40px ${color}66`}}>{pts.total}</div>
        <div style={{fontSize:13,color:"rgba(255,255,255,.6)",
          fontFamily:"Barlow,sans-serif",marginTop:6}}>1 point per action</div>
      </div>
      <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8}}>
        {stats.map((s,i)=>(
          <div key={i} style={{background:"rgba(255,255,255,.05)",borderRadius:14,padding:"12px",
            border:`1px solid ${s.val>0?color+"33":"rgba(255,255,255,.06)"}`}}>
            <div style={{fontSize:24,fontWeight:700,color:s.val>0?color:"rgba(255,255,255,.4)"}}>{s.val}</div>
            <div style={{fontSize:12,color:"rgba(255,255,255,.72)",
              fontFamily:"Barlow,sans-serif",fontWeight:600,marginTop:2}}>{s.label}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── WEEKLY POINTS CHART ─────────────────────────────────────────────
function WeeklyPointsChart({discData, logs, challengeJoinDates, color}){
  const data = buildWeeklyPoints(discData, logs, challengeJoinDates);
  if(data.length < 2) return(
    <div style={{background:"rgba(255,255,255,.04)",border:"1px solid rgba(255,255,255,.08)",
      borderRadius:18,padding:"20px",marginBottom:14,textAlign:"center"}}>
      <div style={{fontSize:14,color:"rgba(255,255,255,.65)",fontFamily:"Barlow,sans-serif"}}>
        📈 Weekly chart appears after 2 weeks of activity
      </div>
    </div>
  );
  const recent = data.slice(-8);
  return(
    <div style={{background:"rgba(255,255,255,.04)",border:"1px solid rgba(255,255,255,.08)",
      borderRadius:18,padding:"16px 16px 12px",marginBottom:14}}>
      <div style={{fontSize:14,letterSpacing:1.5,color:"rgba(255,255,255,.82)",
        fontFamily:"Barlow,sans-serif",fontWeight:700,marginBottom:4}}>📈 POINTS GROWTH</div>
      <div style={{fontSize:13,color:"rgba(255,255,255,.65)",
        fontFamily:"Barlow,sans-serif",marginBottom:14}}>
        Total: {recent[recent.length-1]?.cumulative||0} pts
      </div>
      <ResponsiveContainer width="100%" height={120}>
        <LineChart data={recent} margin={{left:-16,right:8,top:4}}>
          <XAxis dataKey="week" tick={{fill:"rgba(255,255,255,.6)",fontSize:11,
            fontFamily:"Barlow,sans-serif"}} axisLine={false} tickLine={false}
            tickFormatter={w=>w.split("-W")[1]?"W"+w.split("-W")[1]:""}/>
          <YAxis tick={{fill:"rgba(255,255,255,.6)",fontSize:11,
            fontFamily:"Barlow,sans-serif"}} axisLine={false} tickLine={false}/>
          <Tooltip contentStyle={{background:"#1a1a3e",border:`1px solid ${color}55`,
            borderRadius:10,fontFamily:"Barlow,sans-serif",fontSize:13}}
            labelStyle={{color:"rgba(255,255,255,.72)"}} itemStyle={{color}}
            formatter={(v,n)=>[v, n==="cumulative"?"Total pts":"This week"]}/>
          <Line type="monotone" dataKey="cumulative" stroke={color} strokeWidth={2.5}
            dot={{fill:color,r:4,strokeWidth:0}} name="cumulative"/>
          <Line type="monotone" dataKey="pts" stroke={`${color}77`} strokeWidth={1.5}
            strokeDasharray="4 2" dot={false} name="pts"/>
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}

// ─── COMPETITION BOOK (list view) ─────────────────────────────────
function CompetitionBook({uid, profile, onBack}){
  const [competitions, setCompetitions] = useState([]);
  const [loading, setLoading]           = useState(true);
  const [showForm, setShowForm]         = useState(false);
  const [editId, setEditId]             = useState(null);
  const [fName,  setFName]  = useState("");
  const [fPlace, setFPlace] = useState("");
  const [fDate,  setFDate]  = useState("");
  const [fResult,setFResult]= useState("");
  const [fDesc,  setFDesc]  = useState("");
  const [saving, setSaving] = useState(false);
  const C = profile.color;

  useEffect(()=>{
    fbGetCompetitions(uid).then(c=>{setCompetitions(c);setLoading(false);});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  },[uid]);

  function openAdd(){
    setEditId(null);
    setFName(""); setFPlace(""); setFDate(""); setFResult(""); setFDesc("");
    setShowForm(true);
  }
  function openEdit(comp){
    setEditId(comp.id);
    setFName(comp.name||""); setFPlace(comp.place||"");
    setFDate(comp.date||""); setFResult(comp.result||""); setFDesc(comp.desc||"");
    setShowForm(true);
  }
  async function save(){
    if(!fName.trim()) return;
    setSaving(true);
    let updated;
    if(editId){
      updated=competitions.map(c=>c.id===editId
        ?{...c,name:fName.trim(),place:fPlace.trim(),
           date:fDate.trim()||TODAY(),result:fResult.trim(),desc:fDesc.trim()}:c);
    } else {
      updated=[...competitions,{id:Date.now().toString(),name:fName.trim(),
        place:fPlace.trim(),date:fDate.trim()||TODAY(),
        result:fResult.trim(),desc:fDesc.trim()}];
    }
    await fbSaveCompetitions(uid, updated);
    setCompetitions(updated);
    setSaving(false);
    setShowForm(false);
  }
  async function del(id){
    const updated=competitions.filter(c=>c.id!==id);
    await fbSaveCompetitions(uid,updated);
    setCompetitions(updated);
  }

  const sorted=[...competitions].sort((a,b)=>{
    const ms=s=>{const[d,m,y]=(s||"01/01/2000").split("/");return new Date(`${y}-${m}-${d}`).getTime()||0;};
    return ms(b.date)-ms(a.date);
  });

  return(
    <div style={{minHeight:"100vh",background:"linear-gradient(160deg,#0d0d2b,#0f1e35)",
      color:"#fff",fontFamily:"'Archivo Black','Arial Black',sans-serif",maxWidth:480,margin:"0 auto"}}>

      {/* Form modal */}
      {showForm&&(
        <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,.88)",zIndex:300,
          display:"flex",flexDirection:"column",justifyContent:"flex-end",
          backdropFilter:"blur(8px)",overscrollBehavior:"contain"}}
          onClick={()=>setShowForm(false)}>
          <div onClick={e=>e.stopPropagation()} onTouchMove={e=>e.stopPropagation()}
            style={{background:"linear-gradient(160deg,#12123a,#0f1e35)",
              borderRadius:"24px 24px 0 0",padding:"24px 20px 48px",
              border:"1px solid rgba(255,255,255,.1)",borderBottom:"none",
              maxHeight:"92vh",overflowY:"auto",overscrollBehavior:"contain"}}>
            <div style={{width:40,height:4,background:"rgba(255,255,255,.2)",
              borderRadius:99,margin:"0 auto 20px"}}/>
            <div style={{fontSize:20,marginBottom:18}}>{editId?"✏️ Edit Race":"📖 Add Race"}</div>
            {[
              {label:"RACE NAME *",val:fName,set:setFName,ph:"e.g. City Championship 800m"},
              {label:"LOCATION",   val:fPlace,set:setFPlace,ph:"e.g. Helsinki Stadium"},
              {label:"DATE",       val:fDate, set:setFDate, ph:"dd/mm/yyyy"},
              {label:"YOUR RESULT",val:fResult,set:setFResult,ph:"e.g. 2:56 · 1st place"},
            ].map(f=>(
              <div key={f.label} style={{marginBottom:12}}>
                <div style={{fontSize:12,letterSpacing:1.5,color:"rgba(255,255,255,.7)",
                  fontFamily:"Barlow,sans-serif",fontWeight:600,marginBottom:7}}>{f.label}</div>
                <input value={f.val} onChange={e=>f.set(e.target.value)} placeholder={f.ph}
                  style={{width:"100%",minHeight:52,background:"rgba(255,255,255,.07)",
                    border:`1.5px solid ${f.val?"rgba(255,255,255,.3)":"rgba(255,255,255,.1)"}`,
                    borderRadius:12,padding:"0 16px",color:"#fff",
                    fontFamily:"Barlow,sans-serif",fontSize:15,outline:"none",boxSizing:"border-box"}}/>
              </div>
            ))}
            <div style={{marginBottom:20}}>
              <div style={{fontSize:12,letterSpacing:1.5,color:"rgba(255,255,255,.7)",
                fontFamily:"Barlow,sans-serif",fontWeight:600,marginBottom:7}}>NOTES</div>
              <textarea value={fDesc} onChange={e=>setFDesc(e.target.value)}
                placeholder="How did the race go? Conditions, feelings..."
                rows={3} style={{width:"100%",background:"rgba(255,255,255,.07)",
                  border:"1px solid rgba(255,255,255,.1)",borderRadius:12,
                  padding:"12px 16px",color:"#fff",fontFamily:"Barlow,sans-serif",
                  fontSize:15,outline:"none",boxSizing:"border-box",resize:"none",lineHeight:1.6}}/>
            </div>
            <button onClick={save} disabled={saving||!fName.trim()}
              style={{width:"100%",minHeight:56,
                background:fName.trim()?`linear-gradient(90deg,${C},${C}cc)`:"rgba(255,255,255,.08)",
                color:fName.trim()?"#fff":"rgba(255,255,255,.3)",
                borderRadius:14,border:"none",cursor:fName.trim()?"pointer":"default",
                fontFamily:"'Archivo Black',sans-serif",fontSize:16}}>
              {saving?"Saving…":editId?"✓ Save Changes":"＋ Add Race"}
            </button>
          </div>
        </div>
      )}

      {/* Header */}
      <div style={{background:`linear-gradient(90deg,${C},${C}88)`,
        padding:"16px 16px 14px",display:"flex",alignItems:"center",gap:12,
        position:"relative",overflow:"hidden"}}>
        <div style={{position:"absolute",top:-40,right:-20,width:130,height:130,
          background:"rgba(255,255,255,.07)",borderRadius:"50%",pointerEvents:"none"}}/>
        <button onClick={onBack}
          style={{minHeight:46,background:"rgba(0,0,0,.25)",borderRadius:12,
            padding:"0 16px",color:"#fff",fontFamily:"'Archivo Black',sans-serif",
            fontSize:14,border:"none",cursor:"pointer",flexShrink:0}}>
          ← Back
        </button>
        <div style={{flex:1}}>
          <div style={{fontSize:12,letterSpacing:2,color:"rgba(255,255,255,.8)",
            fontFamily:"Barlow,sans-serif",fontWeight:600}}>{profile.name.toUpperCase()}</div>
          <div style={{fontSize:20}}>📖 Race Book</div>
        </div>
        <div style={{fontSize:14,color:"rgba(255,255,255,.8)",
          fontFamily:"Barlow,sans-serif",fontWeight:600,flexShrink:0}}>
          {competitions.length} races
        </div>
      </div>

      <div style={{padding:"14px 14px 50px"}}>
        {/* Add button */}
        <button onClick={openAdd}
          style={{width:"100%",minHeight:54,marginBottom:14,
            background:`linear-gradient(90deg,${C}33,${C}11)`,
            border:`1.5px dashed ${C}77`,borderRadius:16,
            color:C,fontFamily:"'Archivo Black',sans-serif",fontSize:15,
            cursor:"pointer",display:"flex",alignItems:"center",
            justifyContent:"center",gap:10}}>
          <span style={{fontSize:22}}>＋</span> Add Race
        </button>

        {loading&&<div style={{textAlign:"center",padding:"40px",opacity:.6,
          fontFamily:"Barlow,sans-serif",fontSize:15}} className="pl">Loading…</div>}

        {!loading&&sorted.length===0&&(
          <div style={{textAlign:"center",padding:"48px 20px"}}>
            <div style={{fontSize:48,marginBottom:16}}>🏁</div>
            <div style={{fontSize:17,marginBottom:8}}>No races yet</div>
            <div style={{fontSize:14,color:"rgba(255,255,255,.65)",
              fontFamily:"Barlow,sans-serif",lineHeight:1.6}}>
              Tap "Add Race" to record your first competition
            </div>
          </div>
        )}

        {!loading&&sorted.map((comp)=>(
          <div key={comp.id} style={{background:"rgba(255,255,255,.05)",
            border:"1px solid rgba(255,255,255,.09)",
            borderLeft:`4px solid ${C}`,
            borderRadius:16,padding:"14px 14px 14px 16px",marginBottom:10}}>

            <div style={{display:"flex",justifyContent:"space-between",
              alignItems:"flex-start",marginBottom:6}}>
              <div style={{flex:1,minWidth:0}}>
                <div style={{fontSize:18,fontWeight:700,lineHeight:1.2,marginBottom:4}}>
                  {comp.name}
                </div>
                {comp.place&&(
                  <div style={{fontSize:14,color:"rgba(255,255,255,.72)",
                    fontFamily:"Barlow,sans-serif",fontWeight:500,marginBottom:4}}>
                    📍 {comp.place}
                  </div>
                )}
                <div style={{fontSize:13,color:"rgba(255,255,255,.55)",
                  fontFamily:"Barlow,sans-serif"}}>📅 {comp.date}</div>
              </div>
              <div style={{display:"flex",gap:5,flexShrink:0,marginLeft:10}}>
                <button onClick={()=>openEdit(comp)}
                  style={{width:34,height:34,borderRadius:9,
                    background:"rgba(255,255,255,.08)",border:"none",
                    color:"rgba(255,255,255,.75)",fontSize:15,cursor:"pointer"}}>✎</button>
                <button onClick={()=>del(comp.id)}
                  style={{width:34,height:34,borderRadius:9,
                    background:"rgba(255,80,80,.12)",border:"none",
                    color:"rgba(255,120,120,.85)",fontSize:18,cursor:"pointer"}}>×</button>
              </div>
            </div>

            {comp.result&&(
              <div style={{display:"inline-flex",alignItems:"center",gap:8,
                background:`${C}20`,border:`1px solid ${C}44`,
                borderRadius:9,padding:"5px 12px",marginBottom:comp.desc?10:0}}>
                <span style={{fontSize:11,color:"rgba(255,255,255,.6)",
                  fontFamily:"Barlow,sans-serif",fontWeight:600,letterSpacing:1}}>RESULT</span>
                <span style={{fontSize:16,fontWeight:700,color:C,
                  fontFamily:"Barlow,sans-serif"}}>{comp.result}</span>
              </div>
            )}

            {comp.desc&&(
              <div style={{fontSize:14,color:"rgba(255,255,255,.72)",
                fontFamily:"Barlow,sans-serif",fontStyle:"italic",lineHeight:1.6,
                borderTop:"1px solid rgba(255,255,255,.07)",paddingTop:10,marginTop:6}}>
                "{comp.desc}"
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}


// ─── TOTAL POINTS PAGE ──────────────────────────────────────────────
function TotalPointsPage({profile, discData, logs, challengeJoinDates, onBack}){
  const C = profile.color;
  const pts = calcUserPoints(discData, logs, challengeJoinDates.length);

  const breakdown = [
    {label:"Results added",       icon:"🏅", val:pts.results,           desc:"Every result across all disciplines"},
    {label:"Trainings logged",    icon:"💪", val:pts.trainings,          desc:"Sessions recorded in training log"},
    {label:"Challenges joined",   icon:"🎯", val:pts.challengesJoined,   desc:"Challenge entries submitted"},
    {label:"Goals set",           icon:"📌", val:pts.goalsSet,           desc:"Active goals across disciplines"},
    {label:"Goals achieved",      icon:"✅", val:pts.goalsAchieved,      desc:"Goals where PB beats the target"},
  ];

  return(
    <div style={{minHeight:"100vh",background:"linear-gradient(160deg,#0d0d2b,#0f1e35)",
      color:"#fff",fontFamily:"'Archivo Black','Arial Black',sans-serif",maxWidth:480,margin:"0 auto"}}>

      {/* Header */}
      <div style={{background:`linear-gradient(90deg,${C},${C}88)`,
        padding:"16px 16px 14px",display:"flex",alignItems:"center",gap:12,
        position:"relative",overflow:"hidden"}}>
        <div style={{position:"absolute",top:-40,right:-20,width:130,height:130,
          background:"rgba(255,255,255,.07)",borderRadius:"50%",pointerEvents:"none"}}/>
        <button onClick={onBack}
          style={{minHeight:46,background:"rgba(0,0,0,.25)",borderRadius:12,
            padding:"0 16px",color:"#fff",fontFamily:"'Archivo Black',sans-serif",
            fontSize:14,border:"none",cursor:"pointer",flexShrink:0}}>← Back</button>
        <div style={{flex:1}}>
          <div style={{fontSize:12,letterSpacing:2,color:"rgba(255,255,255,.82)",
            fontFamily:"Barlow,sans-serif",fontWeight:600}}>{profile.name.toUpperCase()}</div>
          <div style={{fontSize:20}}>⭐ Total Points</div>
        </div>
      </div>

      <div style={{padding:"20px 14px 50px"}}>

        {/* Big total */}
        <div style={{textAlign:"center",padding:"32px 20px",
          background:`radial-gradient(ellipse at center, ${C}18 0%, transparent 70%)`,
          marginBottom:20}}>
          <div style={{fontSize:14,letterSpacing:2,color:"rgba(255,255,255,.72)",
            fontFamily:"Barlow,sans-serif",fontWeight:600,marginBottom:10}}>
            TOTAL SCORE
          </div>
          <div style={{fontSize:88,fontWeight:900,color:C,lineHeight:1,
            textShadow:`0 0 60px ${C}88, 0 0 120px ${C}44`}}>
            {pts.total}
          </div>
          <div style={{fontSize:16,color:"rgba(255,255,255,.65)",
            fontFamily:"Barlow,sans-serif",marginTop:10}}>
            points earned through activity
          </div>
        </div>

        {/* Weekly chart */}
        <WeeklyPointsChart discData={discData} logs={logs}
          challengeJoinDates={challengeJoinDates} color={C}/>

        {/* Breakdown */}
        <div style={{fontSize:14,letterSpacing:1.5,color:"rgba(255,255,255,.72)",
          fontFamily:"Barlow,sans-serif",fontWeight:700,marginBottom:12}}>
          BREAKDOWN
        </div>

        {breakdown.map((item,i)=>(
          <div key={i} style={{display:"flex",alignItems:"center",gap:14,
            minHeight:68,padding:"0 16px",marginBottom:8,
            background:"rgba(255,255,255,.05)",
            border:`1px solid ${item.val>0?C+"33":"rgba(255,255,255,.07)"}`,
            borderRadius:16}}>
            <div style={{fontSize:28,flexShrink:0}}>{item.icon}</div>
            <div style={{flex:1,minWidth:0}}>
              <div style={{fontSize:15,fontWeight:600}}>{item.label}</div>
              <div style={{fontSize:13,color:"rgba(255,255,255,.65)",
                fontFamily:"Barlow,sans-serif",fontWeight:500,marginTop:2}}>
                {item.desc}
              </div>
            </div>
            <div style={{textAlign:"right",flexShrink:0}}>
              <div style={{fontSize:28,fontWeight:700,
                color:item.val>0?C:"rgba(255,255,255,.25)"}}>{item.val}</div>
              <div style={{fontSize:12,color:"rgba(255,255,255,.55)",
                fontFamily:"Barlow,sans-serif"}}>pts</div>
            </div>
          </div>
        ))}

        {/* How points work */}
        <div style={{marginTop:20,background:"rgba(255,255,255,.04)",
          border:"1px solid rgba(255,255,255,.08)",borderRadius:16,padding:"16px"}}>
          <div style={{fontSize:13,letterSpacing:1.5,color:"rgba(255,255,255,.65)",
            fontFamily:"Barlow,sans-serif",fontWeight:600,marginBottom:10}}>
            HOW POINTS WORK
          </div>
          <div style={{fontSize:14,color:"rgba(255,255,255,.72)",
            fontFamily:"Barlow,sans-serif",lineHeight:1.7}}>
            Every action earns <strong style={{color:C}}>1 point</strong>:
            adding a result, logging a training session, joining a challenge,
            setting a goal, or achieving one. Points update automatically.
          </div>
        </div>
      </div>
    </div>
  );
}

function UserProfile({uid, profile, discData, onDiscDataChange, logs, challengeJoinDates, onBack, onShowHistory, onShowRankings, onShowBook, onShowPoints, onShowRecords, onShowMonthly, onSignOut}){
  const [activeDisc, setActiveDisc] = useState(null);
  const [activeCat,  setActiveCat]  = useState(null);
  const [showSavedToast, setShowSavedToast] = useState(false);
  const toastTimer = useRef(null);

  function triggerToast(){
    if(toastTimer.current) clearTimeout(toastTimer.current);
    setShowSavedToast(true);
    toastTimer.current = setTimeout(()=>setShowSavedToast(false), 2500);
  }

  const discObj = activeDisc ? ALL_DISCS.find(d=>d.id===activeDisc) : null;

  // If a discipline is open — show DetailView
  if(activeDisc && discObj){
    return(
      <>
        <SaveToast show={showSavedToast}/>
        <DetailView
          disc={discObj}
          dData={discData[activeDisc]||{records:[],subGoals:[],goalV:null}}
          uid={uid}
          profile={profile}
          onBack={()=>setActiveDisc(null)}
          onSaved={triggerToast}
          onDiscDataChange={onDiscDataChange}/>
      </>
    );
  }

  const withData    = ALL_DISCS.filter(d=>discData[d.id]?.records?.length>0);
  const totalSessions = ALL_DISCS.reduce((sum,d)=>(discData[d.id]?.records?.length||0)+sum,0);
  const shareUrl    = `${window.location.origin}?athlete=${uid}`;
  const visCats     = activeCat ? CATEGORIES.filter(c=>c.id===activeCat) : CATEGORIES;

  async function shareProfile(){
    if(navigator.share){
      try{ await navigator.share({title:`${profile.name} on Foxvelo`,url:shareUrl}); return; }
      catch(e){ if(e.name==="AbortError") return; }
    }
    navigator.clipboard?.writeText(shareUrl).then(()=>alert("Profile link copied!"));
  }

  return(
    <div style={{minHeight:"100vh",background:"linear-gradient(160deg,#0d0d2b,#0f1e35)",
      color:"#fff",fontFamily:"'Archivo Black','Arial Black',sans-serif",maxWidth:480,margin:"0 auto"}}>
      <SaveToast show={showSavedToast}/>

      {/* Header */}
      <div style={{background:`linear-gradient(135deg,${profile.color},${profile.color}66)`,
        padding:"20px 16px 16px",position:"relative",overflow:"hidden"}}>
        <div style={{position:"absolute",top:-60,right:-30,width:200,height:200,
          background:"rgba(255,255,255,.07)",borderRadius:"50%",pointerEvents:"none"}}/>
        <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:16}}>
          <button onClick={onBack}
            style={{minHeight:44,background:"rgba(0,0,0,.25)",borderRadius:10,
              padding:"0 14px",color:"#fff",fontFamily:"'Archivo Black',sans-serif",
              fontSize:13,border:"none",cursor:"pointer",flexShrink:0}}>← Back</button>
          <div style={{flex:1}}/>
          <button onClick={onShowRankings}
            style={{minHeight:44,background:"rgba(0,0,0,.2)",borderRadius:10,
              padding:"0 12px",color:"#FFD700",fontFamily:"'Archivo Black',sans-serif",
              fontSize:13,border:"none",cursor:"pointer"}}>🏆</button>
          <button onClick={onSignOut}
            style={{minHeight:44,background:"rgba(0,0,0,.2)",borderRadius:10,
              padding:"0 12px",color:"rgba(255,255,255,.82)",fontFamily:"Barlow,sans-serif",
              fontWeight:600,fontSize:12,border:"none",cursor:"pointer"}}>Sign out</button>
        </div>

        <div style={{display:"flex",alignItems:"flex-end",gap:16,marginBottom:16}}>
          <div style={{width:80,height:80,borderRadius:"50%",
            background:`radial-gradient(circle at 30% 30%, ${profile.color}cc, ${profile.color})`,
            display:"flex",alignItems:"center",justifyContent:"center",fontSize:36,
            border:"4px solid rgba(255,255,255,.25)",boxShadow:`0 8px 32px ${profile.color}44`}}>
            {profile.avatar}
          </div>
          <div style={{paddingBottom:4}}>
            <div style={{fontSize:26,letterSpacing:-1,fontWeight:700}}>{profile.name}</div>
            <div style={{fontSize:16,color:"rgba(255,255,255,.8)",fontFamily:"Barlow,sans-serif",fontWeight:500,marginTop:4}}>
              {[profile.age&&`${profile.age} y.o.`,profile.country].filter(Boolean).join(" · ")}
            </div>
          </div>
        </div>

        <div style={{display:"flex",gap:8}}>
          {[
            {label:"Active",val:withData.length,click:null},
            {label:"Sessions",val:totalSessions,click:null},
          ].map((s,i)=>(
            <div key={i} style={{background:"rgba(0,0,0,.2)",borderRadius:12,
              padding:"10px 0",flex:1,textAlign:"center"}}>
              <div style={{fontSize:24,fontWeight:700}}>{s.val}</div>
              <div style={{fontSize:13,color:"rgba(255,255,255,.75)",fontFamily:"Barlow,sans-serif",
                fontWeight:600,letterSpacing:.5,marginTop:2}}>{s.label.toUpperCase()}</div>
            </div>
          ))}
          {/* Total Points — clickable */}
          <button onClick={onShowPoints}
            style={{background:"rgba(0,0,0,.25)",borderRadius:12,
              padding:"10px 0",flex:1,textAlign:"center",border:"none",cursor:"pointer",
              outline:"none",position:"relative",overflow:"hidden"}}>
            <div style={{position:"absolute",inset:0,background:`${profile.color}22`,
              borderRadius:12,border:`1px solid ${profile.color}55`}}/>
            <div style={{fontSize:24,fontWeight:700,color:profile.color,position:"relative"}}>
              {calcUserPoints(discData,logs,challengeJoinDates.length).total}
            </div>
            <div style={{fontSize:11,color:profile.color,fontFamily:"Barlow,sans-serif",
              fontWeight:700,letterSpacing:.5,marginTop:2,position:"relative"}}>
              PTS ›
            </div>
          </button>
        </div>
      </div>

      {/* Action buttons — 3 in a row */}
      <div style={{padding:"14px 14px 0",display:"flex",gap:7}}>
        <button onClick={shareProfile}
          style={{flex:1,minHeight:50,background:`${profile.color}22`,
            border:`1.5px solid ${profile.color}55`,borderRadius:14,
            color:profile.color,fontFamily:"'Archivo Black',sans-serif",
            fontSize:13,cursor:"pointer",display:"flex",flexDirection:"column",
            alignItems:"center",justifyContent:"center",gap:3}}>
          <span style={{fontSize:18}}>🔗</span>
          <span>Share</span>
        </button>
        <button onClick={onShowHistory}
          style={{flex:1,minHeight:50,background:"rgba(255,255,255,.06)",
            border:"1px solid rgba(255,255,255,.1)",borderRadius:14,
            color:"rgba(255,255,255,.85)",fontFamily:"'Archivo Black',sans-serif",
            fontSize:13,cursor:"pointer",display:"flex",flexDirection:"column",
            alignItems:"center",justifyContent:"center",gap:3}}>
          <span style={{fontSize:18}}>📊</span>
          <span>Log</span>
        </button>
        <button onClick={onShowBook}
          style={{flex:1,minHeight:50,background:"rgba(255,255,255,.06)",
            border:"1px solid rgba(255,255,255,.1)",borderRadius:14,
            color:"rgba(255,255,255,.85)",fontFamily:"'Archivo Black',sans-serif",
            fontSize:13,cursor:"pointer",display:"flex",flexDirection:"column",
            alignItems:"center",justifyContent:"center",gap:3}}>
          <span style={{fontSize:18}}>📖</span>
          <span>Races</span>
        </button>
      </div>

      {/* Category filter tabs */}
      <div style={{display:"flex",background:"rgba(255,255,255,.03)",
        borderBottom:"1px solid rgba(255,255,255,.07)",overflowX:"auto",marginTop:12}}>
        {[{id:null,icon:"🏅",name:"All",color:profile.color},
          ...CATEGORIES.map(c=>({id:c.id,icon:c.icon,name:c.name,color:c.color}))
        ].map(c=>{
          const active = activeCat===c.id;
          return(
            <button key={String(c.id)} onClick={()=>setActiveCat(c.id)}
              style={{minHeight:48,padding:"0 12px",background:active?"rgba(255,255,255,.1)":"transparent",
                color:active?(c.color||"#fff"):"rgba(255,255,255,.4)",
                fontFamily:"Barlow,sans-serif",fontWeight:600,fontSize:12,
                whiteSpace:"nowrap",border:"none",cursor:"pointer",
                borderBottom:active?`3px solid ${c.color||profile.color}`:"3px solid transparent",
                transition:".2s",flexShrink:0}}>
              {c.icon} {c.name}
            </button>
          );
        })}
      </div>

      {/* Disciplines grid — ALL disciplines, tappable */}
      <div style={{padding:"14px 14px 50px"}}>
        <div style={{fontSize:13,letterSpacing:2,opacity:.6,fontFamily:"Barlow,sans-serif",
          marginBottom:12}}>
          TAP ANY DISCIPLINE TO ADD RESULTS OR SET GOALS
        </div>

        {visCats.map(cat=>(
          <div key={cat.id} style={{background:"rgba(255,255,255,.04)",
            border:`1px solid ${cat.color}28`,borderRadius:20,padding:"16px",marginBottom:14}}>

            <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:12}}>
              <span style={{fontSize:22}}>{cat.icon}</span>
              <span style={{fontFamily:"'Archivo Black',sans-serif",fontSize:15,color:cat.color}}>
                {cat.name.toUpperCase()}
              </span>
              <span style={{marginLeft:"auto",fontSize:14,fontFamily:"Barlow,sans-serif",opacity:.65}}>
                {cat.disciplines.filter(d=>discData[d.id]?.records?.length>0).length}/{cat.disciplines.length}
              </span>
            </div>

            <div style={{display:"flex",flexDirection:"column",gap:8}}>
              {cat.disciplines.map(d=>{
                const dd    = discData[d.id]||{records:[],goalV:null};
                const recs  = dd.records||[];
                const curV  = bestVal(d, recs);
                const goalV = dd.goalV;
                const startV= recs.length ? recs[0].v : null;
                const p     = calcPct(d, curV, goalV, startV);
                const hasData = curV!=null || goalV!=null;

                return(
                  <button key={d.id} onClick={()=>setActiveDisc(d.id)}
                    style={{display:"flex",alignItems:"center",gap:12,minHeight:60,
                      padding:"0 14px",
                      background:hasData?"rgba(255,255,255,.06)":"rgba(255,255,255,.025)",
                      border:hasData?`1px solid ${cat.color}44`:"1px solid rgba(255,255,255,.07)",
                      borderRadius:14,textAlign:"left",cursor:"pointer",transition:".15s"}}
                    onTouchStart={e=>e.currentTarget.style.background=`${cat.color}22`}
                    onTouchEnd={e=>e.currentTarget.style.background=hasData?"rgba(255,255,255,.06)":"rgba(255,255,255,.025)"}>

                    <div style={{width:72,fontFamily:"Barlow,sans-serif",fontWeight:600,
                      fontSize:12,color:cat.color,flexShrink:0}}>{d.name}</div>

                    <div style={{flex:1}}>
                      {hasData ? (
                        <>
                          <div style={{display:"flex",justifyContent:"space-between",marginBottom:4}}>
                            <span style={{fontSize:15,fontFamily:"Barlow,sans-serif"}}>
                              {curV!=null?fmtVal(d.id,curV):"—"}
                              <span style={{opacity:.65,fontSize:11,marginLeft:4}}>{d.unit}</span>
                            </span>
                            {goalV!=null&&(
                              <span style={{fontSize:14,fontFamily:"Barlow,sans-serif",opacity:.75}}>
                                → {fmtVal(d.id,goalV)}
                              </span>
                            )}
                          </div>
                          {goalV!=null&&curV!=null&&(
                            <div style={{height:4,background:"rgba(255,255,255,.08)",
                              borderRadius:99,overflow:"hidden"}}>
                              <div style={{height:"100%",width:`${p}%`,
                                background:cat.color,borderRadius:99}}/>
                            </div>
                          )}
                          {recs.length>0&&(
                            <div style={{fontSize:13,color:"rgba(255,255,255,.82)",fontFamily:"Barlow,sans-serif",fontWeight:600,
                              marginTop:3}}>
                              {recs.length} result{recs.length!==1?"s":""}
                            </div>
                          )}
                        </>
                      ):(
                        <div style={{fontSize:14,fontFamily:"Barlow,sans-serif",
                          color:`${cat.color}88`}}>
                          Tap to add results ＋
                        </div>
                      )}
                    </div>

                    <div style={{fontSize:20,opacity:.82,flexShrink:0,color:cat.color}}>›</div>
                  </button>
                );
              })}
            </div>
          </div>
        ))}
      </div>

      {/* Subtle access to World Age Records */}
      <div style={{textAlign:"center",paddingTop:20,paddingBottom:8}}>
        <button onClick={onShowMonthly}
          style={{background:"none",border:"none",cursor:"pointer",
            color:"rgba(255,255,255,.38)",fontFamily:"Barlow,sans-serif",
            fontSize:12,letterSpacing:.3,padding:"4px 10px"}}>
          🎬 Exercise of the Month
        </button>
        <button onClick={onShowRecords}
          style={{background:"none",border:"none",cursor:"pointer",
            color:"rgba(255,255,255,.38)",fontFamily:"Barlow,sans-serif",
            fontSize:12,letterSpacing:.3,padding:"4px 10px"}}>
          🌍 World Age Records
        </button>
      </div>
    </div>
  );
}


// ─── PUBLIC PROFILE (when opened via share link) ─────────────────────
function PublicProfile({targetUid, targetDiscId, onClose, myUid}){
  const [profile,setProfile] = useState(null);
  const [discData,setDiscData] = useState(null);
  const [loading,setLoading] = useState(true);

  useEffect(()=>{
    (async()=>{
      try{
        const prof = await fbGetProfile(targetUid);
        if(!prof){setLoading(false);return;}
        setProfile(prof);
        // Public data only — leaderboard bests
        const lbSnap = await getDoc(doc(db,"leaderboard",targetUid));
        if(lbSnap.exists()) setDiscData(lbSnap.data().bests||{});
      }catch(e){console.error(e);}
      setLoading(false);
    })();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  },[targetUid]);

  const focusDisc = targetDiscId ? ALL_DISCS.find(d=>d.id===targetDiscId) : null;
  const C = focusDisc?.catColor||"#FF6B35";

  if(loading) return(
    <div style={{minHeight:"100vh",background:"linear-gradient(160deg,#0d0d2b,#0f1e35)",
      display:"flex",alignItems:"center",justifyContent:"center"}}>
      <div style={{textAlign:"center"}}>
        <div style={{fontSize:44,marginBottom:12}}>🏅</div>
        <div style={{color:"rgba(255,255,255,.72)",fontFamily:"Barlow,sans-serif",fontSize:14}} className="pl">
          Loading athlete…
        </div>
      </div>
    </div>
  );

  if(!profile) return(
    <div style={{minHeight:"100vh",background:"linear-gradient(160deg,#0d0d2b,#0f1e35)",
      display:"flex",alignItems:"center",justifyContent:"center",flexDirection:"column",gap:16}}>
      <div style={{fontSize:13,opacity:.78,fontFamily:"Barlow,sans-serif"}}>Athlete not found</div>
      <Btn onClick={onClose} bg="rgba(255,255,255,.1)" color="#fff"
        style={{borderRadius:12,fontSize:14}}>← Go back</Btn>
    </div>
  );

  return(
    <div style={{minHeight:"100vh",background:"linear-gradient(160deg,#0d0d2b,#0f1e35)",
      color:"#fff",fontFamily:"'Archivo Black','Arial Black',sans-serif",maxWidth:480,margin:"0 auto"}}>

      {/* Header */}
      <div style={{background:`linear-gradient(90deg,${profile.color},${profile.color}88)`,
        padding:"20px 16px 16px",position:"relative",overflow:"hidden"}}>
        <div style={{position:"absolute",top:-40,right:-20,width:150,height:150,
          background:"rgba(255,255,255,.07)",borderRadius:"50%",pointerEvents:"none"}}/>
        <div style={{display:"flex",alignItems:"center",gap:12,marginBottom:4}}>
          <div style={{width:52,height:52,borderRadius:"50%",background:profile.color,
            display:"flex",alignItems:"center",justifyContent:"center",fontSize:24,
            border:"3px solid rgba(255,255,255,.25)"}}>{profile.avatar}</div>
          <div>
            <div style={{fontSize:22}}>{profile.name}</div>
            <div style={{fontSize:12,opacity:.65,fontFamily:"Barlow,sans-serif"}}>
              {[profile.age&&`${profile.age} y.o.`,profile.country].filter(Boolean).join(" · ")}
            </div>
          </div>
        </div>
      </div>

      <div style={{padding:"16px 14px 50px"}}>
        {/* Focus discipline if provided */}
        {focusDisc&&discData&&discData[targetDiscId]!=null&&(
          <div style={{background:`${C}15`,border:`1.5px solid ${C}44`,
            borderRadius:20,padding:"20px 18px",marginBottom:16}} className="fi">
            <div style={{fontSize:13,letterSpacing:2,opacity:.78,fontFamily:"Barlow,sans-serif",marginBottom:4}}>
              {focusDisc.catIcon} {focusDisc.catName.toUpperCase()}
            </div>
            <div style={{fontSize:20,marginBottom:16}}>{focusDisc.name}</div>
            <div style={{textAlign:"center",padding:"16px 0"}}>
              <div style={{fontSize:13,letterSpacing:2,opacity:.75,fontFamily:"Barlow,sans-serif",marginBottom:4}}>
                PERSONAL BEST
              </div>
              <div style={{fontSize:52,color:C,lineHeight:1}}>
                {fmtVal(targetDiscId,discData[targetDiscId])}
              </div>
              <div style={{fontSize:13,opacity:.6,fontFamily:"Barlow,sans-serif",marginTop:4}}>
                {focusDisc.unit}
              </div>
            </div>
          </div>
        )}

        {/* All results */}
        {discData&&(
          <div>
            <div style={{fontSize:13,letterSpacing:2,opacity:.65,fontFamily:"Barlow,sans-serif",marginBottom:12}}>
              ALL PERSONAL BESTS
            </div>
            {CATEGORIES.map(cat=>{
              const catBests = cat.disciplines.filter(d=>discData[d.id]!=null);
              if(!catBests.length) return null;
              return(
                <div key={cat.id} style={{marginBottom:12}}>
                  <div style={{fontSize:14,letterSpacing:1.5,color:cat.color,fontFamily:"Barlow,sans-serif",
                    fontWeight:600,marginBottom:8}}>{cat.icon} {cat.name.toUpperCase()}</div>
                  {catBests.map(d=>(
                    <div key={d.id} style={{display:"flex",justifyContent:"space-between",
                      alignItems:"center",minHeight:44,padding:"0 14px",marginBottom:6,
                      background:targetDiscId===d.id?`${cat.color}18`:"rgba(255,255,255,.04)",
                      border:targetDiscId===d.id?`1px solid ${cat.color}44`:"1px solid rgba(255,255,255,.06)",
                      borderRadius:12}}>
                      <span style={{fontSize:14,fontFamily:"Barlow,sans-serif",color:cat.color}}>{d.name}</span>
                      <span style={{fontSize:16}}>
                        {fmtVal(d.id,discData[d.id])}
                        <span style={{fontSize:11,opacity:.65,marginLeft:4}}>{d.unit}</span>
                      </span>
                    </div>
                  ))}
                </div>
              );
            })}
          </div>
        )}

        {/* CTA */}
        <div style={{background:"rgba(255,255,255,.04)",border:"1px solid rgba(255,255,255,.08)",
          borderRadius:16,padding:"20px",textAlign:"center",marginTop:20}}>
          <div style={{fontSize:16,marginBottom:6}}>🏅 Foxvelo</div>
          <div style={{fontSize:13,opacity:.78,fontFamily:"Barlow,sans-serif",lineHeight:1.6,marginBottom:16}}>
            Track your athletic progress,<br/>set goals and compete globally
          </div>
          <Btn onClick={()=>window.location.href=window.location.origin}
            bg="linear-gradient(90deg,#FF6B35,#FF3CAC)" color="#fff"
            style={{width:"100%",borderRadius:14,fontSize:15}}>
            Create your profile →
          </Btn>
        </div>

        {myUid&&(
          <button onClick={onClose}
            style={{width:"100%",marginTop:12,minHeight:46,background:"rgba(255,255,255,.06)",
              border:"1px solid rgba(255,255,255,.1)",borderRadius:12,color:"rgba(255,255,255,.82)",
              fontFamily:"Barlow,sans-serif",fontWeight:600,fontSize:13,cursor:"pointer"}}>
            ← My profile
          </button>
        )}
      </div>
    </div>
  );
}



// ─── MONTHLY EXERCISE PAGE ──────────────────────────────────────────
function MonthlyExercisePage({uid, profile, onBack}){
  const monthKey = getMonthKey();
  const ex       = getMonthlyExercise(monthKey);
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [url, setUrl]         = useState("");
  const [note, setNote]       = useState("");
  const [saving, setSaving]   = useState(false);
  const [err, setErr]         = useState("");

  const FF="Barlow,sans-serif";
  const C = profile?.color || "#FF6B35";

  useEffect(()=>{
    fbGetMonthly(monthKey)
      .then(e=>{ setEntries(e); setLoading(false); })
      .catch(()=>{ setLoading(false); });
    fbPurgeOldMonthly();
  },[monthKey]);

  const mine = entries.find(e=>e.uid===uid);

  useEffect(()=>{
    if(mine){ setUrl(mine.url||""); setNote(mine.note||""); }
  },[mine]);

  async function submit(){
    setErr("");
    const clean = normaliseVideoUrl(url);
    if(!clean){
      setErr("Paste a YouTube, Instagram, Vimeo or TikTok link.");
      return;
    }
    setSaving(true);
    try{
      const entry = {
        uid, name:profile.name, avatar:profile.avatar,
        color:profile.color, age:parseInt(profile.age)||null,
        country:profile.country||"Unknown",
        url:clean, note:note.trim().slice(0,140),
        exerciseId:ex.id, date:TODAY(), votes:[],
      };
      const next = await fbSubmitMonthly(monthKey, entry);
      setEntries(next);
      logEvent("monthly_submitted",{exercise:ex.id});
    }catch(e){ setErr("Could not save. Try again."); }
    setSaving(false);
  }

  async function remove(){
    setSaving(true);
    try{
      const next = await fbRemoveMonthly(monthKey, uid);
      setEntries(next); setUrl(""); setNote("");
    }catch(e){ setErr("Could not remove."); }
    setSaving(false);
  }

  async function toggleVote(targetUid){
    if(targetUid===uid) return;
    const next = entries.map(e=>{
      if(e.uid!==targetUid) return e;
      const votes = e.votes||[];
      return {...e, votes: votes.includes(uid) ? votes.filter(v=>v!==uid) : [...votes, uid]};
    });
    setEntries(next);
    try{
      await setDoc(doc(db,"monthlySubmissions",monthKey),{entries:next},{merge:true});
    }catch(e){ /* optimistic */ }
  }

  const ranked = [...entries].sort((a,b)=>(b.votes?.length||0)-(a.votes?.length||0));
  const daysLeft = daysLeftInMonth();

  return(
    <div style={{minHeight:"100vh",background:"linear-gradient(160deg,#0d0d2b,#0f1e35)",
      color:"#fff",fontFamily:"'Archivo Black','Arial Black',sans-serif",
      maxWidth:520,margin:"0 auto",padding:"0 16px 40px"}}>

      <div style={{paddingTop:52,paddingBottom:18}}>
        <button onClick={onBack}
          style={{background:"none",border:"none",color:"rgba(255,255,255,.55)",
            fontFamily:FF,fontSize:13,cursor:"pointer",marginBottom:14,padding:0}}>
          ← Back
        </button>
        <div style={{fontSize:25,fontWeight:600,letterSpacing:-.5}}>🎬 Exercise of the Month</div>
        <div style={{fontSize:13,color:"rgba(255,255,255,.55)",fontFamily:FF,marginTop:4}}>
          {monthLabel(monthKey)} · {daysLeft} day{daysLeft!==1?"s":""} left
        </div>
      </div>

      {/* The exercise */}
      <div style={{background:`linear-gradient(135deg,${C}22,rgba(255,255,255,.05))`,
        border:`1px solid ${C}55`,borderRadius:18,padding:"20px 18px",marginBottom:16}}>
        <div style={{fontSize:40,lineHeight:1,marginBottom:10}}>{ex.icon}</div>
        <div style={{fontSize:22,fontWeight:700,marginBottom:6}}>{ex.name}</div>
        <div style={{fontSize:11,color:C,fontFamily:FF,letterSpacing:1.2,
          fontWeight:700,marginBottom:10}}>{ex.focus.toUpperCase()}</div>
        <div style={{fontSize:14,color:"rgba(255,255,255,.8)",fontFamily:FF,lineHeight:1.65}}>
          {ex.desc}
        </div>
      </div>

      {/* Submit form */}
      <div style={{background:"rgba(255,255,255,.06)",border:"1px solid rgba(255,255,255,.1)",
        borderRadius:16,padding:"16px 16px",marginBottom:18}}>
        <div style={{fontSize:11,color:"rgba(255,255,255,.55)",fontFamily:FF,
          letterSpacing:1.4,fontWeight:700,marginBottom:10}}>
          {mine ? "YOUR SUBMISSION" : "SUBMIT YOUR VIDEO"}
        </div>
        <div style={{fontSize:12,color:"rgba(255,255,255,.5)",fontFamily:FF,
          lineHeight:1.6,marginBottom:12}}>
          Upload your clip to YouTube, Instagram, Vimeo or TikTok, then paste the link here.
          Nothing is stored on Foxvelo — only the link.
        </div>
        <input value={url} onChange={e=>setUrl(e.target.value)}
          placeholder="https://youtube.com/..."
          style={{width:"100%",boxSizing:"border-box",background:"rgba(0,0,0,.25)",
            border:"1px solid rgba(255,255,255,.12)",borderRadius:10,padding:"11px 13px",
            color:"rgba(255,255,255,.9)",fontFamily:FF,fontSize:13,outline:"none",marginBottom:8}}/>
        <input value={note} onChange={e=>setNote(e.target.value)} maxLength={140}
          placeholder="Optional note (e.g. 3rd attempt, 2.15 m)"
          style={{width:"100%",boxSizing:"border-box",background:"rgba(0,0,0,.25)",
            border:"1px solid rgba(255,255,255,.12)",borderRadius:10,padding:"11px 13px",
            color:"rgba(255,255,255,.9)",fontFamily:FF,fontSize:13,outline:"none",marginBottom:10}}/>
        {err&&(
          <div style={{fontSize:12,color:"#FF6B6B",fontFamily:FF,marginBottom:10}}>{err}</div>
        )}
        <div style={{display:"flex",gap:8}}>
          <button onClick={submit} disabled={saving}
            style={{flex:1,padding:"11px 0",borderRadius:10,border:"none",
              cursor:saving?"default":"pointer",fontFamily:FF,fontWeight:700,fontSize:14,
              background:saving?"rgba(255,255,255,.15)":C,color:"#000",opacity:saving?.6:1}}>
            {saving ? "Saving…" : mine ? "Update link" : "Submit"}
          </button>
          {mine&&(
            <button onClick={remove} disabled={saving}
              style={{padding:"11px 16px",borderRadius:10,border:"none",cursor:"pointer",
                fontFamily:FF,fontWeight:600,fontSize:13,
                background:"rgba(255,255,255,.08)",color:"rgba(255,255,255,.6)"}}>
              Remove
            </button>
          )}
        </div>
      </div>

      {/* Champions ranking */}
      <div style={{fontSize:11,color:"rgba(255,255,255,.55)",fontFamily:FF,
        letterSpacing:1.4,fontWeight:700,marginBottom:10}}>
        🏆 CHAMPIONS · {entries.length} SUBMISSION{entries.length!==1?"S":""}
      </div>

      {loading&&(
        <div style={{textAlign:"center",padding:"30px 0",color:"rgba(255,255,255,.5)",
          fontFamily:FF,fontSize:14}}>Loading…</div>
      )}
      {!loading&&ranked.length===0&&(
        <div style={{textAlign:"center",padding:"30px 0",color:"rgba(255,255,255,.45)",
          fontFamily:FF,fontSize:14,lineHeight:1.7}}>
          No submissions yet.<br/>Be the first this month!
        </div>
      )}

      <div style={{display:"flex",flexDirection:"column",gap:8}}>
        {ranked.map((e,i)=>{
          const host = videoHost(e.url);
          const voted = (e.votes||[]).includes(uid);
          const medal = i===0?"🥇":i===1?"🥈":i===2?"🥉":null;
          return(
            <div key={e.uid} style={{background:e.uid===uid?`${C}18`:"rgba(255,255,255,.06)",
              border:`1px solid ${e.uid===uid?C+"55":"rgba(255,255,255,.1)"}`,
              borderRadius:14,padding:"12px 14px",display:"flex",alignItems:"center",gap:11}}>
              <div style={{width:28,textAlign:"center",fontSize:medal?18:14,
                fontWeight:700,color:medal?"#fff":"rgba(255,255,255,.4)",flexShrink:0}}>
                {medal||i+1}
              </div>
              <div style={{width:36,height:36,borderRadius:"50%",background:e.color||"#666",
                display:"flex",alignItems:"center",justifyContent:"center",
                fontSize:18,flexShrink:0}}>{e.avatar||"🏃"}</div>
              <div style={{flex:1,minWidth:0}}>
                <div style={{fontSize:14,fontWeight:600,overflow:"hidden",
                  textOverflow:"ellipsis",whiteSpace:"nowrap"}}>
                  {e.name}{e.uid===uid&&" (you)"}
                </div>
                <div style={{fontSize:11,color:"rgba(255,255,255,.5)",fontFamily:FF,marginTop:1}}>
                  {e.age?`${e.age}y · `:""}{e.country}
                  {e.note&&` · ${e.note}`}
                </div>
              </div>
              <a href={e.url} target="_blank" rel="noopener noreferrer"
                style={{fontSize:17,textDecoration:"none",flexShrink:0,padding:"0 4px"}}
                title={host.label}>{host.icon}</a>
              <button onClick={()=>toggleVote(e.uid)}
                disabled={e.uid===uid}
                style={{background:voted?C:"rgba(255,255,255,.08)",border:"none",
                  borderRadius:9,padding:"6px 9px",cursor:e.uid===uid?"default":"pointer",
                  fontFamily:FF,fontSize:12,fontWeight:700,flexShrink:0,
                  color:voted?"#000":"rgba(255,255,255,.65)",opacity:e.uid===uid?.4:1}}>
                ▲ {(e.votes||[]).length}
              </button>
            </div>
          );
        })}
      </div>

      <div style={{textAlign:"center",marginTop:18,fontSize:11,
        color:"rgba(255,255,255,.32)",fontFamily:FF,lineHeight:1.7}}>
        A new exercise appears on the 1st of each month.<br/>
        Submissions are cleared after two months.
      </div>
    </div>
  );
}


// ─── MONTHLY CHAMPIONS (compact card for Rankings tab) ──────────────
function MonthlyChampions({uid, onOpen}){
  const monthKey = getMonthKey();
  const ex       = getMonthlyExercise(monthKey);
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const FF="Barlow,sans-serif";

  useEffect(()=>{
    fbGetMonthly(monthKey).then(e=>{ setEntries(e); setLoading(false); })
                          .catch(()=>setLoading(false));
  },[monthKey]);

  const top = [...entries]
    .sort((a,b)=>(b.votes?.length||0)-(a.votes?.length||0))
    .slice(0,3);

  return(
    <div style={{marginTop:24,paddingTop:20,borderTop:"1px solid rgba(255,255,255,.1)"}}>
      <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",
        marginBottom:4}}>
        <div style={{fontSize:11,color:"rgba(255,255,255,.55)",fontFamily:FF,
          letterSpacing:1.4,fontWeight:700}}>
          🎬 EXERCISE OF THE MONTH
        </div>
        <button onClick={onOpen}
          style={{background:"none",border:"none",cursor:"pointer",padding:0,
            color:"#A78BFA",fontFamily:FF,fontSize:12,fontWeight:700}}>
          Open ›
        </button>
      </div>
      <div style={{fontSize:12,color:"rgba(255,255,255,.42)",fontFamily:FF,
        marginBottom:12}}>
        {ex.icon} {ex.name} · {monthLabel(monthKey)}
      </div>

      {loading&&(
        <div style={{textAlign:"center",padding:"18px 0",color:"rgba(255,255,255,.45)",
          fontFamily:FF,fontSize:13}}>Loading…</div>
      )}
      {!loading&&top.length===0&&(
        <button onClick={onOpen}
          style={{width:"100%",background:"rgba(167,139,250,.1)",
            border:"1px solid rgba(167,139,250,.28)",borderRadius:12,
            padding:"14px 12px",cursor:"pointer",fontFamily:FF,fontSize:13,
            color:"rgba(255,255,255,.7)",lineHeight:1.6}}>
          No submissions yet — be the first champion!
        </button>
      )}

      <div style={{display:"flex",flexDirection:"column",gap:6}}>
        {top.map((e,i)=>{
          const host  = videoHost(e.url);
          const medal = ["🥇","🥈","🥉"][i];
          return(
            <div key={e.uid} style={{background:e.uid===uid?"rgba(167,139,250,.15)":"rgba(255,255,255,.05)",
              border:`1px solid ${e.uid===uid?"rgba(167,139,250,.45)":"rgba(255,255,255,.09)"}`,
              borderRadius:12,padding:"10px 13px",display:"flex",alignItems:"center",gap:10}}>
              <div style={{fontSize:17,flexShrink:0}}>{medal}</div>
              <div style={{width:32,height:32,borderRadius:"50%",background:e.color||"#666",
                display:"flex",alignItems:"center",justifyContent:"center",
                fontSize:16,flexShrink:0}}>{e.avatar||"🏃"}</div>
              <div style={{flex:1,minWidth:0}}>
                <div style={{fontSize:13,fontWeight:600,overflow:"hidden",
                  textOverflow:"ellipsis",whiteSpace:"nowrap"}}>
                  {e.name}{e.uid===uid&&" (you)"}
                </div>
                <div style={{fontSize:11,color:"rgba(255,255,255,.45)",fontFamily:FF}}>
                  {e.age?`${e.age}y · `:""}{e.country}
                </div>
              </div>
              <a href={e.url} target="_blank" rel="noopener noreferrer"
                style={{fontSize:15,textDecoration:"none",flexShrink:0}}
                title={host.label}>{host.icon}</a>
              <div style={{fontSize:12,fontFamily:FF,fontWeight:700,
                color:"rgba(255,255,255,.6)",flexShrink:0}}>
                ▲ {(e.votes||[]).length}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─── USER RECORDS (best mark among all Foxvelo athletes) ────────────
function UserRecords({uid}){
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [ageF, setAgeF]       = useState("all");
  const FF="Barlow,sans-serif";

  useEffect(()=>{
    fbLoadLb().then(e=>{ setEntries(e); setLoading(false); })
              .catch(()=>setLoading(false));
  },[]);

  const records = useMemo(()=>{
    const out=[];
    ALL_DISCS.forEach(d=>{
      const pool = entries
        .filter(e=>e.bests?.[d.id]!=null)
        .filter(e=>{
          if(ageF==="all") return true;
          const ag=AGE_GROUPS.find(a=>a.id===ageF);
          if(!ag||ag.id==="all") return true;
          const a=parseInt(e.age)||0; return a>=ag.min && a<=ag.max;
        });
      if(!pool.length) return;
      const best = pool.reduce((b,e)=>{
        if(!b) return e;
        return d.lowerBetter
          ? (e.bests[d.id] < b.bests[d.id] ? e : b)
          : (e.bests[d.id] > b.bests[d.id] ? e : b);
      }, null);
      out.push({disc:d, holder:best, value:best.bests[d.id], contenders:pool.length});
    });
    return out;
  },[entries, ageF]);

  return(
    <div style={{marginTop:24,paddingTop:20,borderTop:"1px solid rgba(255,255,255,.1)"}}>
      <div style={{fontSize:11,color:"rgba(255,255,255,.55)",fontFamily:FF,
        letterSpacing:1.4,fontWeight:700,marginBottom:4}}>
        ⭐ FOXVELO RECORDS
      </div>
      <div style={{fontSize:12,color:"rgba(255,255,255,.42)",fontFamily:FF,
        marginBottom:12,lineHeight:1.6}}>
        The best mark ever set by a Foxvelo athlete in each discipline.
      </div>

      {/* Age filter */}
      <div style={{display:"flex",flexWrap:"wrap",gap:5,marginBottom:12}}>
        {AGE_GROUPS.slice(0,8).map(a=>(
          <button key={a.id} onClick={()=>setAgeF(a.id)}
            style={{padding:"4px 9px",borderRadius:7,border:"none",cursor:"pointer",
              fontFamily:FF,fontSize:12,fontWeight:600,
              background:ageF===a.id?"#FFD700":"rgba(255,255,255,.09)",
              color:ageF===a.id?"#000":"rgba(255,255,255,.55)"}}>
            {a.label}
          </button>
        ))}
      </div>

      {loading&&(
        <div style={{textAlign:"center",padding:"22px 0",color:"rgba(255,255,255,.5)",
          fontFamily:FF,fontSize:13}}>Loading…</div>
      )}
      {!loading&&records.length===0&&(
        <div style={{textAlign:"center",padding:"22px 0",color:"rgba(255,255,255,.42)",
          fontFamily:FF,fontSize:13}}>No records in this age group yet.</div>
      )}

      <div style={{display:"flex",flexDirection:"column",gap:6}}>
        {records.map(r=>(
          <div key={r.disc.id} style={{background:"rgba(255,255,255,.05)",
            border:"1px solid rgba(255,255,255,.09)",borderRadius:12,
            padding:"10px 13px",display:"flex",alignItems:"center",gap:11}}>
            <div style={{width:34,height:34,borderRadius:"50%",
              background:r.holder.color||"#666",display:"flex",alignItems:"center",
              justifyContent:"center",fontSize:17,flexShrink:0}}>
              {r.holder.avatar||"🏃"}
            </div>
            <div style={{flex:1,minWidth:0}}>
              <div style={{fontSize:11,color:r.disc.catColor,fontFamily:FF,
                fontWeight:700,letterSpacing:.6}}>{r.disc.name.toUpperCase()}</div>
              <div style={{fontSize:13,fontWeight:600,color:"rgba(255,255,255,.85)",
                marginTop:1,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>
                {r.holder.name}{r.holder.id===uid&&" (you)"}
                <span style={{color:"rgba(255,255,255,.4)",fontWeight:400,fontFamily:FF}}>
                  {r.holder.age?` · ${r.holder.age}y`:""}
                </span>
              </div>
            </div>
            <div style={{textAlign:"right",flexShrink:0}}>
              <div style={{fontSize:18,fontWeight:700,color:r.disc.catColor,lineHeight:1.1}}>
                {fmtVal(r.disc.id, r.value)}
              </div>
              <div style={{fontSize:10,color:"rgba(255,255,255,.35)",fontFamily:FF}}>
                of {r.contenders}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── RECORDS PAGE ─────────────────────────────────────────────────────────
const RECORDS_DISCS = [
  {id:"60m",       label:"60m",          cat:"Sprint"},
  {id:"100m",      label:"100m",         cat:"Sprint"},
  {id:"200m",      label:"200m",         cat:"Sprint"},
  {id:"400m",      label:"400m",         cat:"Sprint"},
  {id:"800m",      label:"800m",         cat:"Middle Distance"},
  {id:"1500m",     label:"1500m",        cat:"Middle Distance"},
  {id:"3000m",     label:"3000m",        cat:"Middle Distance"},
  {id:"60mH",      label:"60m Hurdles",  cat:"Hurdles"},
  {id:"high_jump", label:"High Jump",    cat:"Jumps"},
  {id:"long_jump", label:"Long Jump",    cat:"Jumps"},
  {id:"triple_jump",label:"Triple Jump", cat:"Jumps"},
  {id:"shot_put",  label:"Shot Put",     cat:"Throws"},
  {id:"discus",    label:"Discus",       cat:"Throws"},
  {id:"javelin",   label:"Javelin",      cat:"Throws"},
  {id:"ball_throw",label:"Ball Throw",   cat:"Throws"},
];

function RecordsPage({profile, discData, onBack}){
  const [data,    setData]    = useState(null);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState(null);
  const [gender,  setGender]  = useState("boys");
  const [selAge,  setSelAge]  = useState("all");
  const [selDisc, setSelDisc] = useState("800m");
  const [search,  setSearch]  = useState("");

  useEffect(()=>{
    fetch("/age-records.json")
      .then(r=>{ if(!r.ok) throw new Error("Failed to load"); return r.json(); })
      .then(d=>{ setData(d); setLoading(false); })
      .catch(e=>{ setError(e.message); setLoading(false); });
  },[]);

  const GRAD   = "linear-gradient(90deg,#FF6B35,#FF3CAC)";
  const CARD   = "rgba(255,255,255,.06)";
  const BORDER = "rgba(255,255,255,.1)";
  const MUTED  = "rgba(255,255,255,.55)";
  const BRIGHT = "rgba(255,255,255,.88)";
  const FF     = "Barlow,sans-serif";

  // User PB for selected discipline
  const userPB = useMemo(()=>{
    if(!profile || !discData) return null;
    const d = discData[selDisc];
    if(!d?.results?.length) return null;
    return d.results.reduce((best,r)=>(!best||Number(r.value)<Number(best.value)?r:best),null);
  },[profile,discData,selDisc]);

  const ageOpts = ["all","5","6","7","8","9","10","11","12","13","14","15","16","17","18","19"];

  const rows = useMemo(()=>{
    if(!data) return [];
    const disc = (data[gender]||{})[selDisc]||{};
    return Object.entries(disc)
      .filter(([age])=> selAge==="all" || age===selAge)
      .filter(([,rec])=> !search ||
        rec.name.toLowerCase().includes(search.toLowerCase()) ||
        rec.country.toLowerCase().includes(search.toLowerCase()))
      .sort(([a],[b])=>Number(a)-Number(b));
  },[data,gender,selDisc,selAge,search]);

  const cleanMark = m => String(m).replace(/[iA]$/,"").trim();

  return(
    <div style={{minHeight:"100vh",background:"linear-gradient(160deg,#0d0d2b,#0f1e35)",
      color:"#fff",fontFamily:"'Archivo Black','Arial Black',sans-serif",
      maxWidth:520,margin:"0 auto",padding:"0 16px 40px"}}>

      {/* Header */}
      <div style={{paddingTop:52,paddingBottom:20}}>
        {onBack&&(
          <button onClick={onBack}
            style={{background:"none",border:"none",color:MUTED,fontFamily:FF,
              fontSize:13,cursor:"pointer",marginBottom:14,padding:0,letterSpacing:.5}}>
            ← Back
          </button>
        )}
        <div style={{fontSize:26,fontWeight:600,letterSpacing:-.5}}>🌍 World Age Records</div>
        <div style={{fontSize:13,color:MUTED,fontFamily:FF,marginTop:4,lineHeight:1.6}}>
          Official marks only · Ages 5–19 · Boys & Girls
        </div>
        <div style={{fontSize:11,color:"rgba(255,255,255,.3)",fontFamily:FF,marginTop:3}}>
          Source: age-records.125mb.com · D. Eisold · Jun 2026
        </div>
      </div>

      {/* Gender */}
      <div style={{display:"flex",gap:8,marginBottom:14}}>
        {["boys","girls"].map(g=>(
          <button key={g} onClick={()=>setGender(g)}
            style={{flex:1,padding:"10px 0",borderRadius:10,border:"none",cursor:"pointer",
              fontFamily:FF,fontWeight:600,fontSize:14,letterSpacing:.5,
              background:gender===g?GRAD:CARD,color:gender===g?"#fff":MUTED,
              boxShadow:gender===g?"0 2px 14px rgba(255,107,53,.3)":"none"}}>
            {g==="boys"?"♂ Boys":"♀ Girls"}
          </button>
        ))}
      </div>

      {/* Discipline */}
      <div style={{background:CARD,border:"1px solid "+BORDER,borderRadius:14,
        padding:"12px 14px",marginBottom:10}}>
        <div style={{fontSize:10,color:MUTED,fontFamily:FF,letterSpacing:1.5,marginBottom:8}}>
          DISCIPLINE
        </div>
        <div style={{display:"flex",flexWrap:"wrap",gap:5}}>
          {RECORDS_DISCS.map(d=>(
            <button key={d.id} onClick={()=>setSelDisc(d.id)}
              style={{padding:"5px 10px",borderRadius:8,border:"none",cursor:"pointer",
                fontFamily:FF,fontSize:12,fontWeight:600,
                background:selDisc===d.id?GRAD:"rgba(255,255,255,.1)",
                color:selDisc===d.id?"#fff":MUTED}}>
              {d.label}
            </button>
          ))}
        </div>
      </div>

      {/* Age */}
      <div style={{background:CARD,border:"1px solid "+BORDER,borderRadius:14,
        padding:"10px 14px",marginBottom:10}}>
        <div style={{fontSize:10,color:MUTED,fontFamily:FF,letterSpacing:1.5,marginBottom:8}}>AGE</div>
        <div style={{display:"flex",flexWrap:"wrap",gap:4}}>
          {ageOpts.map(a=>(
            <button key={a} onClick={()=>setSelAge(a)}
              style={{minWidth:34,padding:"4px 6px",borderRadius:7,border:"none",cursor:"pointer",
                fontFamily:FF,fontSize:12,fontWeight:600,
                background:selAge===a?"#FF6B35":"rgba(255,255,255,.1)",
                color:selAge===a?"#fff":MUTED}}>
              {a==="all"?"All":a}
            </button>
          ))}
        </div>
      </div>

      {/* Search */}
      <input value={search} onChange={e=>setSearch(e.target.value)}
        placeholder="Search athlete or country…"
        style={{width:"100%",boxSizing:"border-box",background:CARD,
          border:"1px solid "+BORDER,borderRadius:10,padding:"10px 14px",
          color:BRIGHT,fontFamily:FF,fontSize:13,outline:"none",marginBottom:12}}/>

      {/* User PB banner */}
      {userPB&&(
        <div style={{background:"rgba(255,107,53,.12)",border:"1px solid rgba(255,107,53,.3)",
          borderRadius:12,padding:"10px 14px",marginBottom:12,display:"flex",
          alignItems:"center",gap:10}}>
          <div style={{fontSize:20}}>⭐</div>
          <div>
            <div style={{fontSize:10,color:"rgba(255,107,53,.9)",fontFamily:FF,
              letterSpacing:1,fontWeight:600}}>YOUR BEST</div>
            <div style={{fontSize:16,fontWeight:600}}>{cleanMark(String(userPB.value))}</div>
          </div>
        </div>
      )}

      {/* States */}
      {loading&&(
        <div style={{textAlign:"center",padding:"48px 0",color:MUTED,fontFamily:FF,fontSize:14}}>
          Loading records…
        </div>
      )}
      {error&&(
        <div style={{textAlign:"center",padding:"32px 0",color:"#FF6B35",fontFamily:FF,fontSize:13}}>
          Could not load records. Check your connection.
        </div>
      )}

      {/* Records list */}
      {!loading&&!error&&(
        <div style={{display:"flex",flexDirection:"column",gap:8}}>
          {rows.length===0&&(
            <div style={{textAlign:"center",color:MUTED,fontFamily:FF,fontSize:14,padding:"32px 0"}}>
              No records for this selection
            </div>
          )}
          {rows.map(([ageStr,rec])=>(
            <div key={ageStr} style={{background:CARD,border:"1px solid "+BORDER,
              borderRadius:14,padding:"14px 16px"}}>
              <div style={{display:"flex",alignItems:"flex-start",gap:12}}>
                {/* Age badge */}
                <div style={{minWidth:40,height:40,borderRadius:10,background:"rgba(255,255,255,.1)",
                  display:"flex",flexDirection:"column",alignItems:"center",
                  justifyContent:"center",flexShrink:0}}>
                  <div style={{fontSize:17,fontWeight:700,lineHeight:1}}>{ageStr}</div>
                  <div style={{fontSize:9,color:MUTED,fontFamily:FF,letterSpacing:.5}}>AGE</div>
                </div>
                {/* Info */}
                <div style={{flex:1,minWidth:0}}>
                  <div style={{display:"flex",alignItems:"baseline",flexWrap:"wrap",gap:8}}>
                    <div style={{fontSize:21,fontWeight:700,background:GRAD,
                      WebkitBackgroundClip:"text",WebkitTextFillColor:"transparent",lineHeight:1.1}}>
                      {cleanMark(rec.mark)}
                    </div>
                    {rec.implement&&(
                      <span style={{fontSize:10,color:MUTED,fontFamily:FF,
                        background:"rgba(255,255,255,.08)",borderRadius:5,padding:"2px 6px"}}>
                        {rec.implement}
                      </span>
                    )}
                    {userPB&&(
                      <span style={{fontSize:11,color:"rgba(255,107,53,.85)",fontFamily:FF,fontWeight:600}}>
                        You: {cleanMark(String(userPB.value))}
                      </span>
                    )}
                  </div>
                  <div style={{fontSize:14,fontWeight:600,color:BRIGHT,marginTop:2,
                    fontFamily:FF,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>
                    {rec.name}
                  </div>
                  <div style={{display:"flex",flexWrap:"wrap",gap:"3px 10px",marginTop:4}}>
                    {[
                      ["🏳️",rec.country],
                      rec.birth&&["🎂",rec.birth],
                      rec.location&&["📍",rec.location],
                      rec.date&&["📅",rec.date],
                    ].filter(Boolean).map(([icon,val],i)=>(
                      <span key={i} style={{fontSize:11,color:MUTED,fontFamily:FF}}>
                        {icon} {val}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── MAIN (inside ClerkProvider) ─────────────────────────────────────
function Main(){
  const { isLoaded, isSignedIn, user } = useUser();
  const { signOut } = useClerk();
  const [screen,setScreen]       = useState("loading");
  const [profile,setProfile]     = useState(null);
  const [discData,setDiscData]   = useState(emptyDiscData());
  const [shareTarget,setShareTarget] = useState(null); // {uid, discId}
  const [challengeTarget,setChallengeTarget] = useState(null);
  const [mainLogs,setMainLogs]   = useState([]); // cached training logs for points calc
  const [mainChallengeJoinDates,setMainCJD] = useState([]); // cached challenge dates

  const uid = user?.id;

  async function loadUserData(userId){
    try{
      const prof = await fbGetProfile(userId);
      if(!prof){setScreen("create");return;}
      setProfile(prof);
      const data = await fbGetDiscData(userId);
      setDiscData({...emptyDiscData(),...data});
      // Load supporting data for points
      fbGetTrainingLog(userId).then(l=>setMainLogs(l||[]));
      fbLoadChallenges().then(chs=>{
        const dates=[];
        chs.forEach(ch=>(ch.entries||[]).filter(e=>e.uid===userId).forEach(e=>dates.push(e.date)));
        setMainCJD(dates);
      });
      setScreen("dashboard");
    }catch(e){
      console.error("Firestore load error:", e);
      setScreen("create");
    }
  }

  // Track whether we just completed OTP — different from initial loading
  const authJustCompleted = useRef(false);

  // Called by AuthScreen after successful OTP verification
  function handleAuthSuccess(){
    authJustCompleted.current = true;
    logEvent("login",{method:"email_otp"});
    setScreen("loading");
  }

  // Check for /records public route
  const isRecordsPath = typeof window !== "undefined" &&
    window.location.pathname === "/records";

  // Check URL params for public profile sharing
  useEffect(()=>{
    if(isRecordsPath) return; // handled separately
    const params = new URLSearchParams(window.location.search);
    const sharedUid = params.get("athlete");
    const sharedDisc = params.get("disc");
    const sharedChallenge = params.get("challenge");
    if(sharedUid){
      setShareTarget({uid: sharedUid, discId: sharedDisc||null});
    } else if(sharedChallenge){
      setChallengeTarget(sharedChallenge);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  },[]);

  useEffect(()=>{
    if(!isLoaded) return;
    if(!isSignedIn){
      if(authJustCompleted.current) return;
      setScreen("auth");
      return;
    }
    authJustCompleted.current = false;
    if(uid) loadUserData(uid);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  },[isLoaded, isSignedIn, uid]);

  async function handleProfileCreate(fields){
    logEvent("sign_up", {method:"email_otp"});
    logEvent("profile_created", {country: fields.country||"unknown"});
    const prof={
      ...fields,
      email:user.primaryEmailAddress?.emailAddress||"",
      createdAt:TODAY()
    };
    await fbSaveProfile(uid,prof);
    const ed=emptyDiscData();
    await fbSaveDiscData(uid,ed);
    setProfile(prof);setDiscData(ed);setScreen("dashboard");
  }

  async function handleDiscChange(discId,newDData){
    const nd={...discData,[discId]:newDData};
    setDiscData(nd);
    const pts=calcUserPoints(nd,mainLogs,mainChallengeJoinDates.length);
    await fbPublishLb(uid,profile,nd,pts.total);
  }

  async function handleSignOut(){
    await signOut();
    setProfile(null);setDiscData(emptyDiscData());setScreen("auth");
  }

  // If stuck on loading for too long, show retry button
  const [loadingTooLong, setLoadingTooLong] = useState(false);
  useEffect(()=>{
    if(screen!=="loading") { setLoadingTooLong(false); return; }
    const t = setTimeout(()=>setLoadingTooLong(true), 8000);
    return ()=>clearTimeout(t);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  },[screen]);

  // /records public page — render directly with optional logged-in user context
  if(isRecordsPath){
    return(
      <RecordsPage
        profile={profile}
        discData={discData}
        onBack={()=>{ window.location.href="/"; }}
      />
    );
  }

  if(screen==="loading"||!isLoaded)return(
    <div style={{minHeight:"100vh",background:"linear-gradient(160deg,#0d0d2b,#0f1e35)",
      display:"flex",alignItems:"center",justifyContent:"center"}}>
      <div style={{textAlign:"center"}}>
        <div style={{fontSize:44,marginBottom:16}}>🏅</div>
        {loadingTooLong ? (
          <>
            <div style={{color:"rgba(255,255,255,.82)",fontFamily:"Barlow,sans-serif",fontSize:14,marginBottom:20}}>
              Taking too long…
            </div>
            <button onClick={()=>window.location.reload()}
              style={{background:"#FF6B35",border:"none",borderRadius:12,padding:"12px 24px",
                color:"#fff",fontFamily:"'Archivo Black',sans-serif",fontSize:14,cursor:"pointer"}}>
              Reload app
            </button>
          </>
        ) : (
          <div style={{color:"rgba(255,255,255,.72)",fontFamily:"Barlow,sans-serif",fontSize:14}} className="pl">Loading…</div>
        )}
      </div>
    </div>
  );

  // If URL has ?challenge=xxx show challenge detail
  if(challengeTarget){
    return <ChallengeDetail
      challengeId={challengeTarget}
      uid={uid} profile={profile||{name:"Guest",avatar:"🏃",color:"#FF6B35",age:"",country:""}}
      onBack={()=>{
        setChallengeTarget(null);
        window.history.replaceState({},document.title,window.location.pathname);
      }}/>;
  }

  // If URL has ?athlete=xxx show public profile
  if(shareTarget){
    return <PublicProfile
      targetUid={shareTarget.uid}
      targetDiscId={shareTarget.discId}
      onClose={()=>{
        setShareTarget(null);
        window.history.replaceState({},document.title,window.location.pathname);
      }}
      myUid={uid}/>;
  }



  return(
    <>
      {screen==="auth"       && <AuthScreen onSuccess={handleAuthSuccess}/>}
      {screen==="create"     && <CreateProfile onSave={handleProfileCreate}/>}
      {screen==="dashboard"  && profile && (
        <Dashboard uid={uid} profile={profile} discData={discData}
          onDiscDataChange={handleDiscChange}
          onShowHistory={()=>setScreen("history")}
          onShowProfile={()=>setScreen("userprofile")}
          onShowMonthly={()=>setScreen("monthly")}
          onSignOut={handleSignOut}/>
      )}

      {screen==="history"    && profile && (
        <TrainingHistory uid={uid} profile={profile}
          onBack={()=>setScreen("dashboard")}/>
      )}

      {screen==="userprofile" && profile && (
        <UserProfile uid={uid} profile={profile} discData={discData}
          onDiscDataChange={handleDiscChange}
          logs={mainLogs}
          challengeJoinDates={mainChallengeJoinDates}
          onBack={()=>setScreen("dashboard")}
          onShowHistory={()=>setScreen("history")}
          onShowRankings={()=>setScreen("rankings")}
          onShowBook={()=>setScreen("compbook")}
          onShowPoints={()=>setScreen("pointspage")}
          onShowRecords={()=>setScreen("records")}
          onShowMonthly={()=>setScreen("monthly")}
          onSignOut={handleSignOut}/>
      )}
      {screen==="pointspage" && profile && (
        <TotalPointsPage uid={uid} profile={profile} discData={discData}
          logs={mainLogs}
          challengeJoinDates={mainChallengeJoinDates}
          onBack={()=>setScreen("userprofile")}/>
      )}
      {screen==="monthly" && profile && (
        <MonthlyExercisePage uid={uid} profile={profile}
          onBack={()=>setScreen("dashboard")}/>
      )}
      {screen==="records" && (
        <RecordsPage
          profile={profile}
          discData={discData}
          onBack={()=>setScreen(profile?"userprofile":"auth")}
        />
      )}
      {screen==="compbook" && profile && (
        <CompetitionBook uid={uid} profile={profile}
          onBack={()=>setScreen("userprofile")}/>
      )}
    </>
  );
}

// ─── ROOT ────────────────────────────────────────────────────────────
export default function App(){
  return(
    <ClerkProvider publishableKey={CLERK_KEY}>
      <style>{CSS}</style>
      <Main/>
    </ClerkProvider>
  );
}
